import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './Inventory.css';
import './Battle.css';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { DevelopmentPanel } from './Development';
import PendingInventoryPanel, { enrichPendingItems, getPendingBaseItemId } from './PendingInventoryPanel';
import { authButtonStyle, authButtonSmallStyle } from '../constants/authStyles';
import { buildDetailedStatRows, buildEfsunRows } from '../utils/itemStatUtils';
import {
  EFSUN_OPEN_TOOL_ID,
  EFSUN_REROLL_TOOL_ID,
  EFSUN_CUBE_TOOL_ID,
  MAX_WEAPON_EFSUNS,
  MAX_EQUIPMENT_EFSUNS,
  formatEfsunCountLabel,
  getEfsunKindForInventoryType,
  getEfsunKindForSlotType,
  initEfsunConfig,
  isWeaponEfsunBlocked
} from '../utils/armorEfsunUtils';
import {
  formatPlatingRemaining,
  getActivePlating,
  isIngotItemId
} from '../utils/ingotPlatingUtils';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { useCharacter } from '../context/CharacterContext';
import isGridItemEquipped from './inventory/isGridItemEquipped';
import InventoryBagGrid from './inventory/InventoryBagGrid';
import {
  INVENTORY_TYPE_CONFIG,
  DEFAULT_UNLOCKED_SLOTS,
  getBaseItemId,
  normalizeUnlockedCount,
  buildInventoryGridFromBootstrap,
  buildInventoryGridFromTypeRequests,
  canPlaceItemInGrid as canPlaceItemInGridShared,
} from './inventory/inventoryGridBootstrap';
import InventoryItemPopup from './inventory/InventoryItemPopup';
import { ItemImage, isSpriteStripUrl, resolveInventoryDropAnchor, getSlotIndexFromGridPoint } from './inventory/inventoryBagGridUtils';
import { farmFoodConfigFromResponse } from '../utils/farmFoodConfig';
import { fetchInventoryBootstrap } from '../utils/inventoryBootstrapClient';
import { readCraftRecipeBook } from '../utils/craftRecipeBook';
import { tl } from '../utils/inlineI18n';
import SocketSlots from './sockets/SocketSlots';
import Coachmark from './tutorial/Coachmark';
const inventoryMenuBgCssVars = (() => {
  const p = process.env.PUBLIC_URL ?? '';
  return {
    '--inv-slot-bg': `url(${p}/image/menu/kutuuzun.png)`,
    '--inv-adam-bg': `url(${p}/image/menu/adam.png)`,
    '--inv-kutukare-bg': `url(${p}/image/menu/kutukare.png)`
  };
})();

const GRID_COLUMNS = 6;
const GRID_ROWS = 5;
const GRID_PAGE_SIZE = GRID_COLUMNS * GRID_ROWS;
const INVENTORY_PAGE_COUNT = 4;
const GRID_SIZE = GRID_PAGE_SIZE * INVENTORY_PAGE_COUNT;
const EQUIPPED_GRID_COLUMNS = 6;
const EQUIPPED_GRID_ROWS = 5;
const EQUIPPED_GRID_SIZE = EQUIPPED_GRID_COLUMNS * EQUIPPED_GRID_ROWS;
const EQUIPPED_SLOT_MAP = {
  2: { slotType: 'weapon', labelKey: 'slotWeapon', enabled: true },
  3: { slotType: 'helmet', labelKey: 'slotHelmet', enabled: true },
  5: { slotType: 'earring', labelKey: 'slotEarring', enabled: true },
  9: { slotType: 'armor', labelKey: 'slotArmor', enabled: true },
  10: { slotType: 'shield', labelKey: 'slotShield', enabled: true },
  11: { slotType: 'necklace', labelKey: 'slotNecklace', enabled: true },
  17: { slotType: null, labelKey: 'slotBracelet', enabled: false },
  // 4 pot slotu 4. satırda ortalı (6 sütunun 2.-5. sütunları)
  20: { slotType: 'pot1', labelKey: 'slotPot1', enabled: true },
  21: { slotType: 'pot2', labelKey: 'slotPot2', enabled: true },
  22: { slotType: 'pot3', labelKey: 'slotPot3', enabled: true },
  23: { slotType: 'pot4', labelKey: 'slotPot4', enabled: true },
  27: { slotType: 'boot', labelKey: 'slotBoot', enabled: true }
};

// 4 pot slotu; her slota sadece pot konur. Yığın başına en fazla POT_STACK_MAX adet.
const POT_SLOT_TYPES = ['pot1', 'pot2', 'pot3', 'pot4'];
const isPotSlotType = (slotType) => POT_SLOT_TYPES.includes(slotType);
const POT_STACK_MAX = 200;

const SELECT_TITLE_KEYS = {
  weapon: 'selectTitleWeapon',
  armor: 'selectTitleArmor',
  helmet: 'selectTitleHelmet',
  boot: 'selectTitleBoot',
  earring: 'selectTitleEarring',
  necklace: 'selectTitleNecklace',
  shield: 'selectTitleShield',
  pot1: 'selectTitlePot',
  pot2: 'selectTitlePot',
  pot3: 'selectTitlePot',
  pot4: 'selectTitlePot',
  pot: 'selectTitlePot',
};

const EMPTY_CATEGORY_KEYS = {
  weapon: 'slotWeapon',
  armor: 'slotArmor',
  helmet: 'slotHelmet',
  boot: 'slotBoot',
  earring: 'slotEarring',
  necklace: 'slotNecklace',
  shield: 'slotShield',
  pot1: 'slotPot',
  pot2: 'slotPot',
  pot3: 'slotPot',
  pot4: 'slotPot',
  pot: 'slotPot',
  other: 'slotOther',
};

const buildPublicImageUrl = (rawUrl) => {
  if (!rawUrl) return '';
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://') || rawUrl.startsWith('data:')) {
    return rawUrl;
  }
  return rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
};

const Inventory = () => {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const { character: userCharacter, setCharacter, setDepotItemCount, refreshCharacter } = useCharacter();
  const [equipment, setEquipment] = useState(null);
  const [equippedItems, setEquippedItems] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [items, setItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [allInventoryItems, setAllInventoryItems] = useState([]);
  const [inventoryGridSlots, setInventoryGridSlots] = useState(Array(GRID_SIZE).fill(null));
  const [unlockedSlotsCount, setUnlockedSlotsCount] = useState(DEFAULT_UNLOCKED_SLOTS);
  const [currentInventoryPage, setCurrentInventoryPage] = useState(1);
  const [draggedSlotIndex, setDraggedSlotIndex] = useState(null);
  const [draggedInventoryItemKey, setDraggedInventoryItemKey] = useState(null);
  const [draggedEquipSlotType, setDraggedEquipSlotType] = useState(null);
  const [dragOverEquipIndex, setDragOverEquipIndex] = useState(null);
  const [dragOverInventoryIndex, setDragOverInventoryIndex] = useState(null);
  const [touchDropTargetIndex, setTouchDropTargetIndex] = useState(null);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [touchDragGhost, setTouchDragGhost] = useState(null);
  const [activeItemPopup, setActiveItemPopup] = useState(null);
  const [activeEquippedPopup, setActiveEquippedPopup] = useState(null);
  const [sellQuantity, setSellQuantity] = useState(1);
  const [sellLoading, setSellLoading] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [friendGifts, setFriendGifts] = useState([]);
  const [itemCatalog, setItemCatalog] = useState({});
  const [draggedPendingItem, setDraggedPendingItem] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingActionLoading, setPendingActionLoading] = useState(false);
  const [efsunApplyLoading, setEfsunApplyLoading] = useState(false);
  const [platingApplyLoading, setPlatingApplyLoading] = useState(false);
  const [bossStomachOpenLoading, setBossStomachOpenLoading] = useState(false);
  const [fishOpenLoading, setFishOpenLoading] = useState(false);
  const [fishCookLoading, setFishCookLoading] = useState(false);
  const [fishUseLoading, setFishUseLoading] = useState(false);
  const [cropUseLoading, setCropUseLoading] = useState(false);
  const [lootBoostLoading, setLootBoostLoading] = useState(false);
  const [recipeBookLoading, setRecipeBookLoading] = useState(false);
  const [oreSmeltLoading, setOreSmeltLoading] = useState(false);
  const [fishOpeningConfig, setFishOpeningConfig] = useState(null);
  const [farmFoodConfig, setFarmFoodConfig] = useState(null);
  const [miningConfig, setMiningConfig] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [bootstrapMeta, setBootstrapMeta] = useState(null);
  const { setError, setStatus, setWarning, clearError } = usePopupMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = ['development', 'pending'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'inventory';
  const [activeTab, setActiveTab] = useState(requestedTab);
  const username = localStorage.getItem('username');
  const getSlotLabel = (labelKey) => (labelKey ? t(labelKey) : '');
  const getSlotSelectTitle = (slot) => t(SELECT_TITLE_KEYS[slot] || 'selectTitleGeneric');
  const getEmptyCategoryLabel = (slot) => t(EMPTY_CATEGORY_KEYS[slot] || 'slotOther');
  const inventoryGridAreaRef = useRef(null);
  const equippedGridAreaRef = useRef(null);
  const touchStartPointRef = useRef({ x: 0, y: 0 });
  const touchHasMovedRef = useRef(false);
  const lastTouchTargetIndexRef = useRef(null);
  const touchScrollSnapshotRef = useRef(null);
  const skipNextClickRef = useRef(false);
  const draggedPendingItemRef = useRef(null);
  const layoutPersistQueueRef = useRef(Promise.resolve());

  useEffect(() => {
    if (userCharacter?.inventoryUnlockedSlots == null) return;
    const nextCount = Math.min(
      GRID_SIZE,
      Math.max(DEFAULT_UNLOCKED_SLOTS, userCharacter.inventoryUnlockedSlots)
    );
    setUnlockedSlotsCount(nextCount);
  }, [userCharacter?.inventoryUnlockedSlots]);

  const enrichedPendingItems = useMemo(
    () => enrichPendingItems(pendingItems, itemCatalog),
    [pendingItems, itemCatalog]
  );

  useEffect(() => {
    // Depo listesi yüklenmeden 0 yazılmasın (yanlış "depoya gitti" bildirimi olmasın).
    if (!hasLoadedOnce) return;
    setDepotItemCount?.(enrichedPendingItems.length);
  }, [enrichedPendingItems.length, setDepotItemCount, hasLoadedOnce]);

  const isStackableInventoryType = (inventoryType) => (
    inventoryType === 'pots' || inventoryType === 'others'
  );

  const getGridItemCatalogId = (slotItem) => {
    if (!slotItem) return null;
    const catalogId = slotItem.baseItem?.id ?? getPendingBaseItemId(slotItem);
    const parsed = Number(catalogId);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const buildGridItemFromPending = (pendingItem) => {
    const config = INVENTORY_TYPE_CONFIG.find((entry) => entry.inventoryType === pendingItem.inventoryType);
    if (!config || !pendingItem.baseItem) return null;
    return {
      ...pendingItem.item,
      uniqueKey: `${pendingItem.inventoryType}-${pendingItem.userItemId}`,
      inventoryType: pendingItem.inventoryType,
      slotType: config.slotType,
      categoryLabel: getSlotLabel(config.labelKey),
      baseItem: pendingItem.baseItem,
      count: pendingItem.count || pendingItem.item?.count || 1,
      id: pendingItem.userItemId
    };
  };

  const applyPendingClaimLocally = (pendingItem, slotIndex = -1) => {
    const gridItem = buildGridItemFromPending(pendingItem);
    if (!gridItem) return;

    setPendingItems((prev) => prev.filter((item) => item.pendingId !== pendingItem.pendingId));

    setInventoryGridSlots((prev) => {
      const next = [...prev];
      const catalogId = getGridItemCatalogId(gridItem);

      if (isStackableInventoryType(pendingItem.inventoryType) && catalogId != null) {
        const existingIndex = next.findIndex((slotItem) => (
          slotItem
          && slotItem.inventoryType === pendingItem.inventoryType
          && getGridItemCatalogId(slotItem) === catalogId
        ));
        if (existingIndex >= 0) {
          next[existingIndex] = {
            ...next[existingIndex],
            count: (next[existingIndex].count || 1) + (gridItem.count || 1)
          };
          return next;
        }
      }

      if (Number.isInteger(slotIndex) && slotIndex >= 0 && slotIndex < next.length) {
        next[slotIndex] = gridItem;
      }
      return next;
    });
  };

  const isSlotLocked = (index, unlockedCount = unlockedSlotsCount) => (
    Number.isInteger(index) && index >= unlockedCount
  );

  const getCharacterName = (characterId) => {
    if (characterId === 0) return tl('Herkese Uygun', 'All classes');
    if (characterId === 1) return tl('Alp', 'Alp');
    if (characterId === 2) return tl('Kara Alp', 'Dark Alp');
    return tl('Bilinmeyen', 'Unknown');
  };

  const getEffectiveCharacterId = () => userCharacter?.characterId ?? bootstrapMeta?.characterId ?? null;
  const getEffectiveLevel = () => userCharacter?.level ?? bootstrapMeta?.level ?? 1;
  const displayGold = userCharacter?.gold ?? bootstrapMeta?.gold ?? 0;
  const displayRuhParasi = userCharacter?.ruhParasi ?? bootstrapMeta?.ruhParasi ?? 0;

  // Item'ın kullanıcının karakterine uygun olup olmadığını kontrol eden fonksiyon
  const isItemCompatible = (item) => {
    if (!item) return true;
    const characterId = getEffectiveCharacterId();
    if (characterId == null) return true;
    if (item.characterId === 0) return true;
    return item.characterId === characterId;
  };

  // Item'ın kullanıcının level'ına uygun olup olmadığını kontrol eden fonksiyon
  const isLevelSufficient = (item) => {
    if (!item) return true;
    return getEffectiveLevel() >= item.level;
  };

  const slotTypesMatch = (itemSlotType, targetSlotType) => {
    if (!itemSlotType || !targetSlotType) return false;
    if (itemSlotType === targetSlotType) return true;
    if (itemSlotType === 'pot' && isPotSlotType(targetSlotType)) {
      return true;
    }
    return false;
  };

  const getPotBaseItemId = (invItem) => invItem?.potId ?? getBaseItemId(invItem);

  const getEfsunTargetKindForInventoryType = (inventoryType) =>
    getEfsunKindForInventoryType(inventoryType);

  const getEfsunTargetKindForSlotType = (slotType) =>
    getEfsunKindForSlotType(slotType);

  const getEfsunTargetKindForItem = (item) => {
    if (!item || isWeaponEfsunBlocked(item)) return null;
    return getEfsunTargetKindForInventoryType(item.inventoryType)
      || getEfsunTargetKindForSlotType(item.slotType);
  };

  const getEfsunMaxForItem = (item) => {
    const kind = getEfsunTargetKindForItem(item);
    if (!kind) return undefined;
    if (kind === 'weapon') return MAX_WEAPON_EFSUNS;
    return MAX_EQUIPMENT_EFSUNS;
  };

  const renderStatRow = (row, key) => (
    <p key={key}>
      {row.label}: {row.value}
      {row.bonusText ? <span className={row.bonusClass || 'item-plating-stat-bonus'}> {row.bonusText}</span> : null}
    </p>
  );

  const renderItemPlatingBlock = (item) => {
    const plating = getActivePlating(item);
    if (!plating) return null;
    return (
      <p className="inventory-plating-line" style={{ color: plating.color || '#4fc3f7' }}>
        {plating.label || t('platingLabelFallback')}
        {' · '}
        +{plating.bonusPercent}%
        {' · '}
        {formatPlatingRemaining(plating.remainingSec)}
      </p>
    );
  };

  const renderItemNameWithPlating = (name, item, as = 'h4') => {
    const plating = getActivePlating(item);
    const Tag = as;
    if (!plating) return <Tag>{name}</Tag>;
    return (
      <Tag style={{ color: plating.color || undefined }}>
        {name}
        <span className="inventory-plating-name-tag"> {plating.label}</span>
      </Tag>
    );
  };

  const buildItemStatRows = (itemLike) => {
    const plating = getActivePlating(itemLike);
    return buildDetailedStatRows(itemLike, {
      platingBonusPercent: plating?.bonusPercent || 0
    });
  };

  const passesEquipItemRequirements = (slotItem, targetSlotType) => {
    if (!slotItem?.baseItem || !targetSlotType) return false;
    if (!slotTypesMatch(slotItem.slotType, targetSlotType)) return false;
    if (!isItemCompatible(slotItem.baseItem)) return false;
    if (!isLevelSufficient(slotItem.baseItem)) return false;

    if (isPotSlotType(targetSlotType)) {
      // Aynı pot türünün farklı yığınları farklı slotlara takılabilir; aynı yığın iki slota takılamaz.
      const usedInOtherSlot = POT_SLOT_TYPES.some((potSlot) => (
        potSlot !== targetSlotType && getEquippedUserItemId(potSlot) === slotItem.id
      ));
      if (usedInOtherSlot) {
        return false;
      }
    }
    return true;
  };

  const canSwapEquipToSlot = (slotItem, targetSlotType, sourceIndex) => {
    if (!passesEquipItemRequirements(slotItem, targetSlotType)) return false;
    if (!Number.isInteger(sourceIndex) || sourceIndex < 0) return false;

    const equippedItem = equippedItems.find((item) => item.slotType === targetSlotType);
    if (!equippedItem) return false;

    const gridWithSourceCleared = [...inventoryGridSlots];
    gridWithSourceCleared[sourceIndex] = null;
    const result = canPlaceItemInGrid({
      gridSlots: gridWithSourceCleared,
      sourceIndex: null,
      targetIndex: sourceIndex,
      movingItem: { baseItem: equippedItem }
    });
    return result.ok;
  };

  const canEquipItemToSlot = (slotItem, targetSlotType, sourceIndex = null) => {
    if (!passesEquipItemRequirements(slotItem, targetSlotType)) return false;
    if (!getEquippedUserItemId(targetSlotType)) return true;
    if (Number.isInteger(sourceIndex) && sourceIndex >= 0) {
      return canSwapEquipToSlot(slotItem, targetSlotType, sourceIndex);
    }
    return false;
  };

  const getInventoryTypeFromSlotType = (slotType) => {
    const map = {
      weapon: 'weapons',
      armor: 'armors',
      helmet: 'helmets',
      boot: 'boots',
      shield: 'shields',
      earring: 'earrings',
      necklace: 'necklaces',
      pot1: 'pots',
      pot2: 'pots',
      pot3: 'pots',
      pot4: 'pots',
      pot: 'pots'
    };
    return map[slotType] || null;
  };

  const buildItemImageUrl = (rawUrl, isEquippedItem = false) => {
    if (!rawUrl) return '';
    if (isEquippedItem) {
      return buildPublicImageUrl(rawUrl);
    }
    return buildPublicImageUrl(rawUrl);
  };

  const setNativeDragGhost = (event, imageUrl) => {
    if (!event?.dataTransfer || !imageUrl) return;
    const ghostImage = document.createElement('img');
    ghostImage.src = imageUrl;
    ghostImage.width = 48;
    ghostImage.height = 48;
    ghostImage.style.position = 'absolute';
    ghostImage.style.top = '-1000px';
    ghostImage.style.left = '-1000px';
    ghostImage.style.opacity = '0.9';
    document.body.appendChild(ghostImage);
    event.dataTransfer.setDragImage(ghostImage, 24, 24);
    setTimeout(() => {
      if (ghostImage.parentNode) {
        ghostImage.parentNode.removeChild(ghostImage);
      }
    }, 0);
  };

  const getEquippedUserItemId = (slotType) => {
    if (!equipment) return null;
    switch (slotType) {
      case 'weapon':
        return equipment.weaponId;
      case 'armor':
        return equipment.armorId;
      case 'helmet':
        return equipment.helmetId;
      case 'boot':
        return equipment.bootId;
      case 'shield':
        return equipment.shieldId;
      case 'earring':
        return equipment.earringId;
      case 'necklace':
        return equipment.necklaceId;
      case 'pot1':
        return equipment.potId;
      case 'pot2':
        return equipment.potId2;
      case 'pot3':
        return equipment.potId3;
      case 'pot4':
        return equipment.potId4;
      default:
        return null;
    }
  };

  const persistGridLayoutInternal = async (gridSlots) => {
    if (!username) return;
    const slotsPayload = gridSlots
      .map((slotItem, index) => (
        slotItem && !isSlotLocked(index) ? { slotIndex: index, itemKey: slotItem.uniqueKey } : null
      ))
      .filter(Boolean);

    try {
      await axios.post('/api/inventory/layout', {
        username,
        slots: slotsPayload
      });
    } catch (persistError) {
      setError(t('errorLayoutSaveFailed'));
    }
  };

  const persistGridLayout = (gridSlots) => {
    layoutPersistQueueRef.current = layoutPersistQueueRef.current
      .then(() => persistGridLayoutInternal(gridSlots))
      .catch(() => {});
    return layoutPersistQueueRef.current;
  };

  const awaitLayoutPersistIdle = () => layoutPersistQueueRef.current;

  const buildInventoryGridItems = async (currentEquipment, bootstrapData = null) => {
    if (bootstrapData?.inventory && bootstrapData?.catalog) {
      const { gridSlots, allItems, unlockedSlotsCount: nextUnlocked } = await buildInventoryGridFromBootstrap({
        username,
        bootstrapData,
        equipment: currentEquipment,
        t,
      });
      setUnlockedSlotsCount(nextUnlocked);
      setAllInventoryItems(allItems);
      setInventoryGridSlots(gridSlots);
      return;
    }

    const typeRequests = INVENTORY_TYPE_CONFIG.map(async (config) => {
      const [inventoryResponse, itemsResponse] = await Promise.all([
        axios.get(`/api/inventory?username=${username}&type=${config.inventoryType}`),
        axios.get(`/api/items?type=${config.inventoryType}`)
      ]);

      const userItems = inventoryResponse.data?.success ? (inventoryResponse.data.items || []) : [];
      const baseItems = itemsResponse.data?.success ? (itemsResponse.data.items || []) : [];

      return userItems.map((invItem) => {
        const baseItemId = getBaseItemId(invItem);
        const baseItem = baseItems.find((item) => item.id === baseItemId);
        if (!baseItem) return null;

        return {
          ...invItem,
          uniqueKey: `${config.inventoryType}-${invItem.id}`,
          inventoryType: config.inventoryType,
          slotType: config.slotType,
          categoryLabel: t(config.labelKey),
          baseItem,
          count: invItem.count || 1
        };
      }).filter(Boolean);
    });

    const { gridSlots, allItems, unlockedSlotsCount: nextUnlocked } = await buildInventoryGridFromTypeRequests({
      username,
      equipment: currentEquipment,
      typeRequests,
    });
    setUnlockedSlotsCount(nextUnlocked);
    setAllInventoryItems(allItems);
    setInventoryGridSlots(gridSlots);
  };

  const resolveDraggedInventoryIndex = (event) => {
    if (draggedSlotIndex !== null) return draggedSlotIndex;

    let payloadKey = draggedInventoryItemKey;
    if (!payloadKey && event?.dataTransfer) {
      payloadKey = event.dataTransfer.getData('text/inventory-item-key') || null;
    }
    if (!payloadKey) return null;

    const resolvedIndex = inventoryGridSlots.findIndex(
      (slotItem) => slotItem && slotItem.uniqueKey === payloadKey
    );
    return resolvedIndex >= 0 ? resolvedIndex : null;
  };

  const getFootprintIndexes = (anchorIndex, width, height) => {
    if (!Number.isInteger(anchorIndex) || anchorIndex < 0 || anchorIndex >= GRID_SIZE) return null;
    const pageStart = Math.floor(anchorIndex / GRID_PAGE_SIZE) * GRID_PAGE_SIZE;
    const localIndex = anchorIndex - pageStart;
    const row = Math.floor(localIndex / GRID_COLUMNS);
    const col = localIndex % GRID_COLUMNS;
    if (col + width > GRID_COLUMNS || row + height > GRID_ROWS) return null;

    const indexes = [];
    for (let dy = 0; dy < height; dy += 1) {
      for (let dx = 0; dx < width; dx += 1) {
        const targetLocalIndex = (row + dy) * GRID_COLUMNS + (col + dx);
        indexes.push(pageStart + targetLocalIndex);
      }
    }
    return indexes;
  };

  const buildSpanGridStyle = (span) => ({
    gridColumnEnd: `span ${span.width}`,
    gridRowEnd: `span ${span.height}`,
    aspectRatio: `${span.width} / ${span.height}`,
    zIndex: 2
  });

  const computeGridCoverage = (gridSlots) => {
    const coveredIndexes = new Set();
    const spanByIndex = {};
    for (let pageIndex = 0; pageIndex < INVENTORY_PAGE_COUNT; pageIndex += 1) {
      const pageStartIndex = pageIndex * GRID_PAGE_SIZE;
      const pageSlots = gridSlots.slice(pageStartIndex, pageStartIndex + GRID_PAGE_SIZE);
      const pageCoverage = computePageCoverage(pageSlots, pageStartIndex);
      Object.entries(pageCoverage.spanByIndex).forEach(([idx, span]) => {
        spanByIndex[Number(idx)] = span;
      });
      pageCoverage.coveredIndexes.forEach((idx) => coveredIndexes.add(idx));
    }
    return { coveredIndexes, spanByIndex };
  };

  const canPlaceItemInGrid = (args) => canPlaceItemInGridShared({
    ...args,
    unlockedCount: args.unlockedCount ?? unlockedSlotsCount,
  });

  const isInventoryDropBlocked = (targetIndex, event) => {
    if (draggedEquipSlotType) {
      const equippedItem = equippedItems.find((item) => item.slotType === draggedEquipSlotType);
      const result = canPlaceItemInGrid({
        gridSlots: inventoryGridSlots,
        sourceIndex: null,
        targetIndex,
        movingItem: { baseItem: equippedItem || {} }
      });
      return !result.ok;
    }

    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex === null) return true;
    if (sourceIndex === targetIndex) return false;
    if (canApplyEfsunToolToInventoryTarget(event, targetIndex)) return false;
    if (canApplyIngotToInventoryTarget(event, targetIndex)) return false;
    if (canMergePotStacks(inventoryGridSlots[sourceIndex], inventoryGridSlots[targetIndex])) return false;
    const movingItem = inventoryGridSlots[sourceIndex];
    const result = canPlaceItemInGrid({
      gridSlots: inventoryGridSlots,
      sourceIndex,
      targetIndex,
      movingItem
    });
    return !result.ok;
  };

  const handleGridDragStart = (event, index) => {
    const slotItem = inventoryGridSlots[index];
    if (!slotItem) return;
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/inventory-item-key', slotItem.uniqueKey);
      const ghostUrl = buildItemImageUrl(slotItem?.baseItem?.imageUrl);
      setNativeDragGhost(event, ghostUrl);
    }
    setDraggedSlotIndex(index);
    setDraggedInventoryItemKey(slotItem.uniqueKey);
  };

  // İki pot yığını: aynı pot, farklı yığın ve hedef dolu değilse birleştirilebilir.
  const isSamePotStack = (sourceItem, targetItem) => (
    !!sourceItem && !!targetItem
    && sourceItem.inventoryType === 'pots' && targetItem.inventoryType === 'pots'
    && sourceItem.id !== targetItem.id
    && getPotBaseItemId(sourceItem) === getPotBaseItemId(targetItem)
  );
  const canMergePotStacks = (sourceItem, targetItem) => (
    isSamePotStack(sourceItem, targetItem) && (targetItem.count || 1) < POT_STACK_MAX
  );

  const handleMergePotStacks = async (sourceId, targetId) => {
    try {
      clearError();
      const response = await axios.post('/api/inventory/merge-pots', { username, sourceId, targetId });
      if (!response.data?.success) {
        setError(response.data?.message || t('errorPotMergeFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorPotMergeFailed'));
    }
    await refreshInventoryContent();
  };

  const handleGridDrop = (event, targetIndex) => {
    if (draggedEquipSlotType) {
      if (isInventoryDropBlocked(targetIndex, event)) {
        setError(t('errorDropBlocked'));
        setDraggedEquipSlotType(null);
        setDragOverInventoryIndex(null);
        return;
      }
      handleUnequipToInventory(draggedEquipSlotType, targetIndex);
      setDraggedEquipSlotType(null);
      setDragOverInventoryIndex(null);
      return;
    }
    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex === null || sourceIndex === targetIndex) return;
    const potSource = inventoryGridSlots[sourceIndex];
    const potTarget = inventoryGridSlots[targetIndex];
    if (isSamePotStack(potSource, potTarget)) {
      setDragOverInventoryIndex(null);
      setDraggedSlotIndex(null);
      setDraggedInventoryItemKey(null);
      if (canMergePotStacks(potSource, potTarget)) {
        handleMergePotStacks(potSource.id, potTarget.id);
      } else {
        setError(t('errorPotStackFull', { max: POT_STACK_MAX }));
      }
      return;
    }
    const { tool } = getDraggedEfsunTool(event);
    const targetItem = inventoryGridSlots[targetIndex];
    const targetKind = getEfsunTargetKindForItem(targetItem);
    if (tool && targetKind && targetItem.id) {
      setDragOverInventoryIndex(null);
      setDraggedSlotIndex(null);
      setDraggedInventoryItemKey(null);
      handleApplyEfsunToItem(targetKind, targetItem.id, tool);
      return;
    }
    const ingotId = getDraggedIngotId(event);
    if (ingotId && targetKind && targetItem?.id) {
      setDragOverInventoryIndex(null);
      setDraggedSlotIndex(null);
      setDraggedInventoryItemKey(null);
      handleApplyPlatingToItem(targetKind, targetItem.id, ingotId);
      return;
    }
    if (isInventoryDropBlocked(targetIndex, event)) {
      setError(t('errorDropBlocked'));
      setDragOverInventoryIndex(null);
      setDraggedSlotIndex(null);
      setDraggedInventoryItemKey(null);
      return;
    }

    const next = [...inventoryGridSlots];
    const draggedItem = next[sourceIndex];
    next[sourceIndex] = null;
    next[targetIndex] = draggedItem;
    setInventoryGridSlots(next);
    persistGridLayout(next);
    setDragOverInventoryIndex(null);
    setDraggedSlotIndex(null);
    setDraggedInventoryItemKey(null);
  };

  const handleInventorySlotDragOver = (event, index) => {
    if (draggedSlotIndex === null && !draggedInventoryItemKey && !draggedEquipSlotType) return;
    event.preventDefault();
    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex === index) {
      setDragOverInventoryIndex(null);
      return;
    }
    setDragOverInventoryIndex(index);
  };

  const handleUnequipToInventory = async (slotType, targetIndex) => {
    const inventoryType = getInventoryTypeFromSlotType(slotType);
    const equippedUserItemId = getEquippedUserItemId(slotType);
    if (!inventoryType || !equippedUserItemId) {
      setError(t('errorMoveToInventoryFailed'));
      return;
    }

    try {
      clearError();
      const response = await axios.post('/api/unequip', {
        username,
        itemType: slotType,
        targetSlot: targetIndex
      });

      if (!response.data.success) {
        setError(response.data.message || t('errorUnequipFailed'));
        return;
      }

      if (response.data.miningPaused) {
        setStatus(t('statusMiningPaused'));
      }

      await loadInventoryBootstrap({ silent: true });
      const movedItemKey = `${inventoryType}-${equippedUserItemId}`;

      setInventoryGridSlots((prev) => {
        const currentIndex = prev.findIndex((item) => item && item.uniqueKey === movedItemKey);
        if (currentIndex === -1 || currentIndex === targetIndex) return prev;
        const movedItem = prev[currentIndex];
        const canPlace = canPlaceItemInGrid({
          gridSlots: prev,
          sourceIndex: currentIndex,
          targetIndex,
          movingItem: movedItem
        });
        if (!canPlace.ok) {
          setError(t('errorDropBlocked'));
          return prev;
        }
        const next = [...prev];
        next[currentIndex] = null;
        next[targetIndex] = movedItem;
        persistGridLayout(next);
        return next;
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || t('errorUnequipFailed');
      setError(errorMessage);
    }
  };

  const handleEquipFromGrid = async (slotItem, targetSlotType, sourceIndex = null) => {
    if (!slotItem || !targetSlotType) return;
    const expectedSlotConfig = Object.values(EQUIPPED_SLOT_MAP).find(
      (config) => config.slotType === targetSlotType
    );
    const expectedLabel = expectedSlotConfig?.labelKey
      ? getSlotLabel(expectedSlotConfig.labelKey)
      : targetSlotType;
    const isSwap = Boolean(getEquippedUserItemId(targetSlotType));

    if (!canEquipItemToSlot(slotItem, targetSlotType, sourceIndex)) {
      if (!slotTypesMatch(slotItem.slotType, targetSlotType)) {
        setError(t('errorSlotTypeMismatch', { slotLabel: expectedLabel, category: slotItem.categoryLabel }));
      } else if (!isItemCompatible(slotItem.baseItem)) {
        setError(t('errorCharacterMismatch'));
      } else if (!isLevelSufficient(slotItem.baseItem)) {
        setError(t('errorLevelInsufficient', { level: slotItem.baseItem.level }));
      } else if (isSwap) {
        setError(t('errorDropBlocked'));
      } else if (isPotSlotType(targetSlotType)) {
        setError(t('errorSamePotTwice'));
      } else {
        setError(t('errorEquipSlotMismatch'));
      }
      return;
    }

    try {
      clearError();
      if (isSwap) {
        const response = await axios.post('/api/equip-swap', {
          username,
          itemType: targetSlotType,
          newItemId: slotItem.id,
          sourceSlotIndex: sourceIndex
        });
        if (response.data.success) {
          await loadInventoryBootstrap({ silent: true });
          setActiveItemPopup(null);
        } else {
          setError(response.data.message || t('errorEquipFailed'));
        }
        return;
      }

      const response = await axios.post('/api/equip', {
        username,
        itemType: targetSlotType,
        itemId: slotItem.id
      });

      if (response.data.success) {
        await loadInventoryBootstrap({ silent: true });
        setActiveItemPopup(null);
      } else {
        setError(response.data.message || t('errorEquipFailed'));
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || t('errorEquipFailed');
      setError(errorMessage);
    }
  };

  const handleEquipSlotDragOver = (event, slotConfig, gridIndex) => {
    if (!slotConfig?.enabled || !slotConfig.slotType) return;
    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex === null || !inventoryGridSlots[sourceIndex]) return;
    event.preventDefault();
    setDragOverEquipIndex(gridIndex);
  };

  const handleEquipSlotDrop = async (event, slotConfig) => {
    if (!slotConfig?.enabled || !slotConfig.slotType) return;
    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex === null) return;
    const draggedItem = inventoryGridSlots[sourceIndex];
    const equippedItem = equippedItems.find((item) => item.slotType === slotConfig.slotType);
    const tool = getEfsunToolForOther(draggedItem?.baseItem?.id);
    const targetKind = getEfsunTargetKindForItem(equippedItem);
    setDragOverEquipIndex(null);
    setDraggedSlotIndex(null);
    setDraggedInventoryItemKey(null);
    if (tool && targetKind && equippedItem) {
      await handleApplyEfsunToItem(targetKind, getEquippedUserItemId(slotConfig.slotType), tool);
      return;
    }
    const ingotId = Number(draggedItem?.baseItem?.id);
    if (isIngotItemId(ingotId) && targetKind && equippedItem) {
      await handleApplyPlatingToItem(targetKind, getEquippedUserItemId(slotConfig.slotType), ingotId);
      return;
    }
    await handleEquipFromGrid(draggedItem, slotConfig.slotType, sourceIndex);
  };

  const getSlotIndexFromPoint = (x, y) => {
    const elementAtPoint = document.elementFromPoint(x, y);
    const slotElement = elementAtPoint?.closest?.('[data-slot-index]');
    if (slotElement) {
      const slotIndex = Number(slotElement.dataset.slotIndex);
      if (Number.isInteger(slotIndex) && slotIndex >= 0 && slotIndex < GRID_SIZE) {
        return slotIndex;
      }
    }

    return getSlotIndexFromGridPoint(x, y, inventoryGridAreaRef.current, currentInventoryPage);
  };

  const getEquipSlotTypeFromPoint = (x, y) => {
    const elementAtPoint = document.elementFromPoint(x, y);
    const slotElement = elementAtPoint?.closest?.('[data-equip-slot-type]');
    if (!slotElement) return null;
    return slotElement.dataset.equipSlotType || null;
  };

  const getEquipGridIndexFromPoint = (x, y) => {
    const elementAtPoint = document.elementFromPoint(x, y);
    const slotElement = elementAtPoint?.closest?.('[data-equip-grid-index]');
    if (!slotElement) return null;
    const gridIndex = Number(slotElement.dataset.equipGridIndex);
    if (!Number.isInteger(gridIndex)) return null;
    return gridIndex;
  };

  const getInventoryPageFromPoint = (x, y) => {
    const elementAtPoint = document.elementFromPoint(x, y);
    const pageElement = elementAtPoint?.closest?.('[data-inventory-page]');
    if (!pageElement) return null;
    const page = Number(pageElement.dataset.inventoryPage);
    if (!Number.isInteger(page) || page < 1 || page > INVENTORY_PAGE_COUNT) return null;
    return page;
  };

  const lockTouchDragScroll = () => {
    if (touchScrollSnapshotRef.current) return;
    touchScrollSnapshotRef.current = {
      bodyOverflow: document.body.style.overflow,
      bodyTouchAction: document.body.style.touchAction,
      bodyOverscroll: document.body.style.overscrollBehavior,
      htmlOverflow: document.documentElement.style.overflow,
      htmlTouchAction: document.documentElement.style.touchAction,
      htmlOverscroll: document.documentElement.style.overscrollBehavior
    };
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.touchAction = 'none';
    document.documentElement.style.overscrollBehavior = 'none';
  };

  const unlockTouchDragScroll = () => {
    const snapshot = touchScrollSnapshotRef.current;
    if (!snapshot) return;
    document.body.style.overflow = snapshot.bodyOverflow;
    document.body.style.touchAction = snapshot.bodyTouchAction;
    document.body.style.overscrollBehavior = snapshot.bodyOverscroll;
    document.documentElement.style.overflow = snapshot.htmlOverflow;
    document.documentElement.style.touchAction = snapshot.htmlTouchAction;
    document.documentElement.style.overscrollBehavior = snapshot.htmlOverscroll;
    touchScrollSnapshotRef.current = null;
  };

  const handleGridTouchStart = (event, index, slotItem) => {
    if (!slotItem) return;

    const touch = event.touches?.[0];
    if (!touch) return;
    lockTouchDragScroll();
    const ghostUrl = buildItemImageUrl(slotItem?.baseItem?.imageUrl);
    touchStartPointRef.current = { x: touch.clientX, y: touch.clientY };
    touchHasMovedRef.current = false;
    lastTouchTargetIndexRef.current = null;
    setTouchDropTargetIndex(null);
    setDragOverEquipIndex(null);
    setIsTouchDragging(true);
    setTouchDragGhost({
      imageUrl: ghostUrl,
      x: touch.clientX,
      y: touch.clientY
    });
    setDraggedSlotIndex(index);
  };

  const handleEquipTouchStart = (event, slotConfig, equippedItem) => {
    if (!slotConfig?.enabled || !slotConfig.slotType || !equippedItem) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    lockTouchDragScroll();
    const ghostUrl = buildItemImageUrl(equippedItem?.imageUrl, true);
    touchStartPointRef.current = { x: touch.clientX, y: touch.clientY };
    touchHasMovedRef.current = false;
    lastTouchTargetIndexRef.current = null;
    setTouchDropTargetIndex(null);
    setDragOverInventoryIndex(null);
    setDragOverEquipIndex(null);
    setIsTouchDragging(true);
    setTouchDragGhost({
      imageUrl: ghostUrl,
      x: touch.clientX,
      y: touch.clientY
    });
    setDraggedEquipSlotType(slotConfig.slotType);
  };

  const handleGridTouchMove = (event) => {
    if (draggedSlotIndex === null && !draggedEquipSlotType && !isPendingDragActive()) return;

    const touch = event.touches?.[0];
    if (!touch) return;
    setTouchDragGhost((prev) => (prev ? {
      ...prev,
      x: touch.clientX,
      y: touch.clientY
    } : prev));

    const targetPage = getInventoryPageFromPoint(touch.clientX, touch.clientY);
    if (targetPage && targetPage !== currentInventoryPage) {
      touchHasMovedRef.current = true;
      setCurrentInventoryPage(targetPage);
      setTouchDropTargetIndex(null);
      setDragOverInventoryIndex(null);
      requestAnimationFrame(() => {
        const refreshedTargetIndex = getSlotIndexFromPoint(touch.clientX, touch.clientY);
        const canUseTargetIndex = refreshedTargetIndex !== null
          && (draggedSlotIndex === null || refreshedTargetIndex !== draggedSlotIndex);
        if (canUseTargetIndex) {
          setTouchDropTargetIndex(refreshedTargetIndex);
          setDragOverInventoryIndex(refreshedTargetIndex);
          lastTouchTargetIndexRef.current = refreshedTargetIndex;
        }
      });
    }

    const dx = Math.abs(touch.clientX - touchStartPointRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartPointRef.current.y);
    if (dx > 6 || dy > 6) {
      touchHasMovedRef.current = true;
    }

    const targetIndex = getSlotIndexFromPoint(touch.clientX, touch.clientY);
    const canUseTargetIndex = targetIndex !== null
      && (draggedSlotIndex === null || targetIndex !== draggedSlotIndex);
    setTouchDropTargetIndex(
      canUseTargetIndex ? targetIndex : null
    );
    setDragOverInventoryIndex(
      canUseTargetIndex ? targetIndex : null
    );
    if (canUseTargetIndex) {
      lastTouchTargetIndexRef.current = targetIndex;
    }

    const equipSlotType = getEquipSlotTypeFromPoint(touch.clientX, touch.clientY);
    if (equipSlotType) {
      const equipGridIndex = getEquipGridIndexFromPoint(touch.clientX, touch.clientY);
      setDragOverEquipIndex(equipGridIndex);
    } else {
      setDragOverEquipIndex(null);
    }
  };

  const handleInventoryTabDragOver = (event, page) => {
    if (!isTouchDragging) return;
    if (draggedSlotIndex === null && !draggedInventoryItemKey && !draggedEquipSlotType) return;
    event.preventDefault();
    if (page !== currentInventoryPage) {
      setCurrentInventoryPage(page);
      setDragOverInventoryIndex(null);
      setTouchDropTargetIndex(null);
    }
  };

  const handleGridTouchEnd = (event) => {
    if (draggedSlotIndex === null && !draggedEquipSlotType && !isPendingDragActive()) return;

    const changedTouch = event.changedTouches?.[0];
    if (touchHasMovedRef.current && changedTouch) {
      const activePendingItem = draggedPendingItemRef.current || draggedPendingItem;
      if (activePendingItem) {
        const activePage = inventoryGridAreaRef.current?.querySelector('.inventory-grid-page.active');
        const gridRect = activePage?.getBoundingClientRect();
        const releaseInsideGrid = Boolean(
          gridRect
          && changedTouch.clientX >= gridRect.left
          && changedTouch.clientX <= gridRect.right
          && changedTouch.clientY >= gridRect.top
          && changedTouch.clientY <= gridRect.bottom
        );
        let dropCell = getSlotIndexFromPoint(changedTouch.clientX, changedTouch.clientY);
        if (dropCell === null && !releaseInsideGrid) {
          dropCell = touchDropTargetIndex ?? dragOverInventoryIndex ?? lastTouchTargetIndexRef.current;
        }
        if (dropCell !== null) {
          const anchorIndex = resolvePendingDropAnchor(activePendingItem, dropCell);
          if (anchorIndex === null) {
            setError(t('errorDropBlocked'));
          } else {
            handleClaimPendingItem(activePendingItem, anchorIndex);
            skipNextClickRef.current = true;
          }
        }
      } else {
        const equipSlotType = getEquipSlotTypeFromPoint(changedTouch.clientX, changedTouch.clientY);
        if (equipSlotType && draggedSlotIndex !== null) {
          const draggedItem = inventoryGridSlots[draggedSlotIndex];
          const tool = getEfsunToolForOther(draggedItem?.baseItem?.id);
          const equippedItem = equippedItems.find((item) => item.slotType === equipSlotType);
          const targetKind = getEfsunTargetKindForItem(equippedItem);
          if (tool && targetKind && equippedItem) {
            handleApplyEfsunToItem(targetKind, getEquippedUserItemId(equipSlotType), tool);
          } else if (isIngotItemId(draggedItem?.baseItem?.id) && targetKind && equippedItem) {
            handleApplyPlatingToItem(targetKind, getEquippedUserItemId(equipSlotType), Number(draggedItem.baseItem.id));
          } else {
            handleEquipFromGrid(draggedItem, equipSlotType, draggedSlotIndex);
          }
          skipNextClickRef.current = true;
        } else {
          const targetIndexFromPoint = getSlotIndexFromPoint(changedTouch.clientX, changedTouch.clientY);
          const targetIndex = targetIndexFromPoint ?? touchDropTargetIndex ?? dragOverInventoryIndex ?? lastTouchTargetIndexRef.current;
          if (targetIndex !== null && (draggedSlotIndex === null || targetIndex !== draggedSlotIndex)) {
            const draggedItem = draggedSlotIndex !== null ? inventoryGridSlots[draggedSlotIndex] : null;
            const tool = getEfsunToolForOther(draggedItem?.baseItem?.id);
            const targetItem = inventoryGridSlots[targetIndex];
            const targetKind = getEfsunTargetKindForItem(targetItem);
            if (draggedSlotIndex !== null && isSamePotStack(draggedItem, targetItem)) {
              // Dokunarak sürükleme: pot yığını aynı potun üstüne bırakılınca birleşir (taşınmaz, depoya gitmez).
              if (canMergePotStacks(draggedItem, targetItem)) {
                handleMergePotStacks(draggedItem.id, targetItem.id);
              } else {
                setError(t('errorPotStackFull', { max: POT_STACK_MAX }));
              }
              skipNextClickRef.current = true;
            } else if (tool && targetKind && targetItem.id) {
              handleApplyEfsunToItem(targetKind, targetItem.id, tool);
              skipNextClickRef.current = true;
            } else if (isIngotItemId(draggedItem?.baseItem?.id) && targetKind && targetItem?.id) {
              handleApplyPlatingToItem(targetKind, targetItem.id, Number(draggedItem.baseItem.id));
              skipNextClickRef.current = true;
            } else if (isInventoryDropBlocked(targetIndex)) {
              setError(t('errorDropBlocked'));
            } else if (draggedEquipSlotType) {
              handleUnequipToInventory(draggedEquipSlotType, targetIndex);
              skipNextClickRef.current = true;
            } else {
              const next = [...inventoryGridSlots];
              const draggedItem = next[draggedSlotIndex];
              next[draggedSlotIndex] = null;
              next[targetIndex] = draggedItem;
              setInventoryGridSlots(next);
              persistGridLayout(next);
              skipNextClickRef.current = true;
            }
          }
        }
      }
    }

    touchHasMovedRef.current = false;
    lastTouchTargetIndexRef.current = null;
    setTouchDropTargetIndex(null);
    setDragOverInventoryIndex(null);
    setDragOverEquipIndex(null);
    setIsTouchDragging(false);
    setTouchDragGhost(null);
    setDraggedSlotIndex(null);
    setDraggedInventoryItemKey(null);
    setDraggedEquipSlotType(null);
    clearActivePendingDrag();
    unlockTouchDragScroll();
  };

  const handleGridTouchCancel = () => {
    unlockTouchDragScroll();
    touchHasMovedRef.current = false;
    lastTouchTargetIndexRef.current = null;
    setTouchDropTargetIndex(null);
    setDragOverInventoryIndex(null);
    setDragOverEquipIndex(null);
    setIsTouchDragging(false);
    setTouchDragGhost(null);
    setDraggedSlotIndex(null);
    setDraggedInventoryItemKey(null);
    setDraggedEquipSlotType(null);
    clearActivePendingDrag();
  };

  const handleGridItemClick = (slotItem, index) => {
    if (skipNextClickRef.current) {
      skipNextClickRef.current = false;
      return;
    }

    if (!slotItem || !inventoryGridAreaRef.current) {
      setActiveItemPopup(null);
      return;
    }

    const isSameSlotOpen = activeItemPopup && activeItemPopup.index === index;
    if (isSameSlotOpen) {
      setActiveItemPopup(null);
      return;
    }

    setActiveItemPopup({
      index,
      item: slotItem
    });
  };

  const handleEquippedItemClick = (gridIndex, slotConfig, equippedItem) => {
    // Boş kuşanma yuvasına tıklayınca hiçbir şey açılmaz.
    if (!slotConfig || !equippedItem || !equippedGridAreaRef.current) {
      setActiveEquippedPopup(null);
      return;
    }

    const isSameSlotOpen = activeEquippedPopup && activeEquippedPopup.index === gridIndex;
    if (isSameSlotOpen) {
      setActiveEquippedPopup(null);
      return;
    }

    setActiveEquippedPopup({
      index: gridIndex,
      slotLabel: getSlotLabel(slotConfig.labelKey),
      item: equippedItem
    });
  };

  const handleClosePopupsOnCardClickCapture = (event) => {
    if (!activeItemPopup && !activeEquippedPopup) return;
    const clickedSlot = event.target?.closest?.('.inventory-slot-box');
    if (clickedSlot) return;
    const clickedItemPopup = event.target?.closest?.('.inventory-item-popup');
    if (clickedItemPopup) return;
    setActiveItemPopup(null);
    setActiveEquippedPopup(null);
  };

  const getItemGridSize = (slotItem) => {
    if (slotItem?.slotType === 'armor') {
      return { width: 1, height: 2 };
    }
    const rawW = Number(slotItem?.baseItem?.gridWidth || 1);
    const rawH = Number(slotItem?.baseItem?.gridHeight || 1);
    const width = Number.isFinite(rawW) ? Math.max(1, Math.min(rawW, GRID_COLUMNS)) : 1;
    const height = Number.isFinite(rawH) ? Math.max(1, Math.min(rawH, GRID_ROWS)) : 1;
    return { width, height };
  };

  const getEquippedItemGridSize = (equippedItem) => {
    if (equippedItem?.slotType === 'armor') {
      return { width: 1, height: 2 };
    }
    const rawW = Number(equippedItem?.gridWidth || 1);
    const rawH = Number(equippedItem?.gridHeight || 1);
    const width = Number.isFinite(rawW) ? Math.max(1, Math.min(rawW, EQUIPPED_GRID_COLUMNS)) : 1;
    const height = Number.isFinite(rawH) ? Math.max(1, Math.min(rawH, EQUIPPED_GRID_ROWS)) : 1;
    return { width, height };
  };

  const computePageCoverage = (pageSlots, pageStartIndex) => {
    const coveredIndexes = new Set();
    const spanByIndex = {};

    pageSlots.forEach((slotItem, localIndex) => {
      if (!slotItem) return;
      const absoluteIndex = pageStartIndex + localIndex;
      if (coveredIndexes.has(absoluteIndex)) return;

      const { width, height } = getItemGridSize(slotItem);
      const footprint = getFootprintIndexes(absoluteIndex, width, height);
      if (!footprint) {
        spanByIndex[absoluteIndex] = { width: 1, height: 1 };
        return;
      }

      spanByIndex[absoluteIndex] = { width, height };
      footprint.slice(1).forEach((idx) => coveredIndexes.add(idx));
    });

    return { coveredIndexes, spanByIndex };
  };

  const computeEquippedCoverage = (equippedBySlotType) => {
    const coveredIndexes = new Set();
    const spanByIndex = {};

    for (let gridIndex = 1; gridIndex <= EQUIPPED_GRID_SIZE; gridIndex += 1) {
      const slotConfig = EQUIPPED_SLOT_MAP[gridIndex];
      if (!slotConfig?.enabled || !slotConfig.slotType) continue;
      const equippedItem = equippedBySlotType.get(slotConfig.slotType);
      if (!equippedItem) continue;

      const { width, height } = getEquippedItemGridSize(equippedItem);
      if (width === 1 && height === 1) continue;

      const localIndex = gridIndex - 1;
      const row = Math.floor(localIndex / EQUIPPED_GRID_COLUMNS);
      const col = localIndex % EQUIPPED_GRID_COLUMNS;
      if (col + width > EQUIPPED_GRID_COLUMNS || row + height > EQUIPPED_GRID_ROWS) continue;

      let canExpand = true;
      const candidateIndexes = [];
      for (let dy = 0; dy < height && canExpand; dy += 1) {
        for (let dx = 0; dx < width; dx += 1) {
          if (dx === 0 && dy === 0) continue;
          const targetGridIndex = (row + dy) * EQUIPPED_GRID_COLUMNS + (col + dx) + 1;
          const targetSlotConfig = EQUIPPED_SLOT_MAP[targetGridIndex];
          if (coveredIndexes.has(targetGridIndex) || targetSlotConfig?.enabled) {
            canExpand = false;
            break;
          }
          candidateIndexes.push(targetGridIndex);
        }
      }
      if (!canExpand) continue;

      spanByIndex[gridIndex] = { width, height };
      candidateIndexes.forEach((idx) => coveredIndexes.add(idx));
    }

    // Zırh slotu (9 üst, 15 alt) boşken de 2 kare yükseklikte gösterilsin
    const armorAnchor = 9;
    const armorCfg = EQUIPPED_SLOT_MAP[armorAnchor];
    if (armorCfg?.slotType === 'armor' && !equippedBySlotType.get('armor')) {
      spanByIndex[armorAnchor] = { width: 1, height: 2 };
      coveredIndexes.add(15);
    }

    return { coveredIndexes, spanByIndex };
  };

  useEffect(() => {
    setActiveItemPopup(null);
    setActiveEquippedPopup(null);
  }, [currentInventoryPage]);

  useEffect(() => {
    if (draggedSlotIndex === null) {
      setDragOverEquipIndex(null);
      setDragOverInventoryIndex(null);
    }
  }, [draggedSlotIndex]);

  useEffect(() => {
    if (!draggedInventoryItemKey) {
      setDragOverInventoryIndex(null);
    }
  }, [draggedInventoryItemKey]);

  useEffect(() => {
    if (!draggedEquipSlotType) {
      setDragOverEquipIndex(null);
      setDragOverInventoryIndex(null);
    }
  }, [draggedEquipSlotType]);

  useEffect(() => {
    if (!draggedPendingItem) {
      setDragOverInventoryIndex(null);
    }
  }, [draggedPendingItem]);

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    loadInventoryBootstrap();
    axios.get('/api/fishing/config')
      .then((res) => {
        if (res.data?.success && res.data?.config?.opening) {
          setFishOpeningConfig(res.data.config.opening);
        }
      })
      .catch(() => {});
    axios.get('/api/farming/config')
      .then((res) => {
        if (res.data?.success) {
          const foodCfg = farmFoodConfigFromResponse(res.data);
          if (foodCfg) setFarmFoodConfig(foodCfg);
        }
      })
      .catch(() => {});
    axios.get('/api/mining/config')
      .then((res) => {
        if (res.data?.success && res.data?.config) {
          setMiningConfig(res.data.config);
        }
      })
      .catch(() => {});
  }, [username, navigate]);

  useEffect(() => {
    setActiveTab(requestedTab);
  }, [requestedTab]);

  useEffect(() => {
    if (!username || !hasLoadedOnce) return;
    refreshInventoryContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab, username, hasLoadedOnce]);

  useEffect(() => {
    if (activeItemPopup?.item) {
      setSellQuantity(1);
    }
  }, [activeItemPopup]);

  const syncCharacterFromBootstrap = (data, goldOverride) => {
    if (!data || !setCharacter) return;
    setBootstrapMeta({
      gold: goldOverride ?? data.gold ?? 0,
      ruhParasi: data.ruhParasi ?? 0,
      characterId: data.characterId,
      level: data.level,
      inventoryUnlockedSlots: data.inventoryUnlockedSlots
    });
    if (userCharacter) {
      setCharacter({
        ...userCharacter,
        gold: goldOverride ?? data.gold ?? userCharacter.gold,
        ruhParasi: data.ruhParasi ?? userCharacter.ruhParasi,
        inventoryUnlockedSlots: data.inventoryUnlockedSlots ?? userCharacter.inventoryUnlockedSlots
      });
    }
  };

  const applyBootstrapData = async (data) => {
    if (data.efsunConfig) {
      initEfsunConfig(data.efsunConfig);
    }
    const equipmentData = data.equipment || null;
    setEquipment(equipmentData);
    setEquippedItems(data.equippedItems || []);
    setPendingItems(data.pending || []);
    setItemCatalog(data.catalog || {});
    if (data.layout?.unlockedSlots != null) {
      setUnlockedSlotsCount(normalizeUnlockedCount(data.layout.unlockedSlots));
    }
    setBootstrapMeta({
      gold: data.gold ?? 0,
      ruhParasi: data.ruhParasi ?? 0,
      characterId: data.characterId,
      level: data.level,
      inventoryUnlockedSlots: data.inventoryUnlockedSlots
    });
    await buildInventoryGridItems(equipmentData, data);
  };

  const fetchPendingItems = async () => {
    const response = await axios.get(`/api/inventory/pending?username=${username}`);
    if (response.data?.success) {
      setPendingItems(response.data.items || []);
    }
  };

  const fetchInventoryData = async () => {
    try {
      const eqResponse = await axios.get(
        `/api/equipment?username=${username}`
      );
      if (eqResponse.data.success) {
        setEquipment(eqResponse.data.equipment);
      }

      const equippedResponse = await axios.get(
        `/api/equipped-items?username=${username}`
      );
      if (equippedResponse.data.success && equippedResponse.data.items) {
        setEquippedItems(equippedResponse.data.items);
      } else {
        setEquippedItems([]);
      }

      await buildInventoryGridItems(eqResponse.data.equipment || null);
      await fetchPendingItems();
    } catch (err) {
      setError(t('errorLoadFailed'));
    }
  };

  const fetchData = async () => {
    await refreshCharacter().catch(() => {});
    await fetchInventoryData();
  };

  const loadInventoryBootstrap = async ({ silent = false, refreshGold = false, goldOverride } = {}) => {
    if (!username) return null;
    // Arka plan yenilemesinde (takma/çıkarma/satın alma sonrası) ekranı kaplayan perde gösterilmez.
    if (!silent) setRefreshing(true);
    if (!silent && !hasLoadedOnce) {
      setPendingLoading(true);
    }
    try {
      const response = await fetchInventoryBootstrap(username);
      if (response.data?.success) {
        await applyBootstrapData(response.data);
        if (refreshGold || goldOverride != null) {
          syncCharacterFromBootstrap(response.data, goldOverride);
        }
        return response.data;
      }
      setError(t('errorLoadFailed'));
      return null;
    } catch (err) {
      setError(silent ? t('errorUpdateFailed') : t('errorLoadFailed'));
      return null;
    } finally {
      setRefreshing(false);
      setHasLoadedOnce(true);
      setPendingLoading(false);
    }
  };

  const refreshInventoryContent = async ({ refreshGold = false, goldOverride } = {}) => {
    await loadInventoryBootstrap({ silent: true, refreshGold, goldOverride });
  };

  const fetchFriendGifts = async () => {
    try {
      const response = await axios.get('/api/friends/gifts');
      if (response.data?.success) setFriendGifts(response.data.gifts || []);
    } catch (_) {
      // depo hediyeleri opsiyonel
    }
  };

  useEffect(() => {
    if (username) fetchFriendGifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const handleClaimFriendGift = async (gift) => {
    if (!gift?.giftId) return;
    setPendingActionLoading(true);
    clearError();
    try {
      const response = await axios.post('/api/friends/gifts/claim', { giftId: gift.giftId });
      if (response.data?.success) {
        setFriendGifts((prev) => prev.filter((g) => g.giftId !== gift.giftId));
        setStatus(t('statusClaimSuccess'));
        await refreshInventoryContent({ refreshGold: gift.kind === 'gold' });
        if (gift.kind === 'gold') await refreshCharacter().catch(() => {});
      } else {
        setError(response.data?.message || t('errorClaimFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorClaimFailed'));
    } finally {
      setPendingActionLoading(false);
    }
  };

  const handleClaimPendingItem = async (pendingItem, slotIndex = -1) => {
    if (!pendingItem?.pendingId || !username) return;
    setPendingActionLoading(true);
    clearError();
    try {
      await awaitLayoutPersistIdle();
      const response = await axios.post('/api/inventory/pending/claim', {
        username,
        pendingId: pendingItem.pendingId,
        slotIndex
      });
      if (response.data?.success) {
        const placedSlot = response.data.slotIndex;
        if (Number.isInteger(placedSlot) && placedSlot >= 0) {
          applyPendingClaimLocally(pendingItem, placedSlot);
        } else {
          setPendingItems((prev) => prev.filter((item) => item.pendingId !== pendingItem.pendingId));
        }
        setStatus(response.data.message || t('statusClaimSuccess'));
        await refreshInventoryContent();
      } else {
        setError(response.data?.message || t('errorClaimFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorClaimFailed'));
    } finally {
      setPendingActionLoading(false);
    }
  };

  const handleSellPendingItem = async (pendingItem) => {
    if (!pendingItem?.item || !username) return;
    setPendingActionLoading(true);
    clearError();
    try {
      const response = await axios.post('/api/inventory/sell', {
        username,
        inventoryType: pendingItem.inventoryType,
        userItemId: pendingItem.userItemId,
        count: pendingItem.item.count || 1
      });
      if (response.data?.success) {
        setStatus(response.data.message || t('statusSellComplete'));
        const newGold = typeof response.data.newGold === 'number' ? response.data.newGold : undefined;
        await refreshInventoryContent({ refreshGold: true, goldOverride: newGold });
      } else {
        setError(response.data?.message || t('errorSellFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorSellFailed'));
    } finally {
      setPendingActionLoading(false);
    }
  };

  const handleDiscardPendingItem = async (pendingItem) => {
    if (!pendingItem?.pendingId || !username) return;
    setPendingActionLoading(true);
    clearError();
    try {
      const response = await axios.post('/api/inventory/pending/discard', {
        username,
        pendingId: pendingItem.pendingId
      });
      if (response.data?.success) {
        setStatus(response.data.message || t('statusDiscarded'));
        await refreshInventoryContent();
      } else {
        setError(response.data?.message || t('errorDiscardFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorDiscardFailed'));
    } finally {
      setPendingActionLoading(false);
    }
  };

  const getPendingMovingItem = (pendingItem) => {
    const config = INVENTORY_TYPE_CONFIG.find((entry) => entry.inventoryType === pendingItem.inventoryType);
    return {
      baseItem: pendingItem.baseItem,
      slotType: config?.slotType
    };
  };

  const resolveDraggedPendingItem = (event) => {
    if (draggedPendingItemRef.current) return draggedPendingItemRef.current;
    if (draggedPendingItem) return draggedPendingItem;
    if (!event?.dataTransfer) return null;
    const source = event.dataTransfer.getData('text/pending-source');
    const pendingIdRaw = event.dataTransfer.getData('text/pending-id')
      || (source === 'pending' ? event.dataTransfer.getData('text/plain') : '');
    const pendingId = Number(pendingIdRaw);
    if (!Number.isInteger(pendingId) || pendingId <= 0) return null;
    return enrichedPendingItems.find((item) => item.pendingId === pendingId) || null;
  };

  const isPendingDragActive = () => Boolean(
    draggedPendingItemRef.current || draggedPendingItem
  );

  const setActivePendingDrag = (pendingItem) => {
    draggedPendingItemRef.current = pendingItem;
    setDraggedPendingItem(pendingItem);
  };

  const clearActivePendingDrag = () => {
    draggedPendingItemRef.current = null;
    setDraggedPendingItem(null);
    setDragOverInventoryIndex(null);
  };

  const handlePendingAreaDragOver = (event) => {
    if (!isPendingDragActive()) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  };

  const resolvePendingDropAnchor = (pendingItem, dropIndex) => {
    const movingItem = getPendingMovingItem(pendingItem);
    if (!movingItem?.baseItem) return null;
    return resolveInventoryDropAnchor({
      dropIndex,
      movingItem,
      canPlaceAtAnchor: (anchorIndex) => canPlaceItemInGrid({
        gridSlots: inventoryGridSlots,
        sourceIndex: null,
        targetIndex: anchorIndex,
        movingItem
      }).ok
    });
  };

  const canPlacePendingAtSlot = (pendingItem, targetIndex) => (
    resolvePendingDropAnchor(pendingItem, targetIndex) !== null
  );

  const isPendingDropBlocked = (targetIndex, event) => {
    const pendingItem = event ? resolveDraggedPendingItem(event) : draggedPendingItem;
    if (!pendingItem) return true;
    return !canPlacePendingAtSlot(pendingItem, targetIndex);
  };

  const handlePendingDragStart = (event, pendingItem) => {
    if (!pendingItem?.baseItem) return;
    setActivePendingDrag(pendingItem);
    const ghostUrl = buildItemImageUrl(pendingItem.baseItem.imageUrl);
    setNativeDragGhost(event, ghostUrl);
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/pending-source', 'pending');
      event.dataTransfer.setData('text/pending-id', String(pendingItem.pendingId));
      event.dataTransfer.setData('text/plain', String(pendingItem.pendingId));
    }
  };

  const handlePendingPressStart = () => {
    lockTouchDragScroll();
  };

  const handlePendingPressEnd = () => {
    unlockTouchDragScroll();
  };

  const handlePendingTouchStart = (event, pendingItem) => {
    if (!pendingItem?.baseItem || pendingActionLoading) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    setActivePendingDrag(pendingItem);
    const ghostUrl = buildItemImageUrl(pendingItem.baseItem.imageUrl);
    touchStartPointRef.current = { x: touch.clientX, y: touch.clientY };
    touchHasMovedRef.current = false;
    lastTouchTargetIndexRef.current = null;
    setTouchDropTargetIndex(null);
    setDragOverInventoryIndex(null);
    setIsTouchDragging(true);
    setTouchDragGhost({
      imageUrl: ghostUrl,
      x: touch.clientX,
      y: touch.clientY
    });
  };

  const handlePendingInventorySlotDragOver = (event, index) => {
    if (!isPendingDragActive()) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = isPendingDropBlocked(index, event) ? 'none' : 'move';
    }
    setDragOverInventoryIndex(index);
  };

  const handlePendingDropOnGrid = async (event, targetIndex) => {
    event.preventDefault();
    event.stopPropagation();
    const pendingItem = resolveDraggedPendingItem(event);
    clearActivePendingDrag();
    if (!pendingItem) return;
    const anchorIndex = resolvePendingDropAnchor(pendingItem, targetIndex);
    if (anchorIndex === null) {
      setError(t('errorDropBlocked'));
      return;
    }
    await handleClaimPendingItem(pendingItem, anchorIndex);
  };

  const isPendingTabDropBlocked = (targetIndex, event) => {
    const pendingItem = event ? resolveDraggedPendingItem(event) : draggedPendingItem;
    if (pendingItem) {
      return !canPlacePendingAtSlot(pendingItem, targetIndex);
    }
    return isInventoryDropBlocked(targetIndex, event);
  };

  const handlePendingTabSlotDragOver = (event, index) => {
    if (isPendingDragActive()) {
      handlePendingInventorySlotDragOver(event, index);
      return;
    }
    handleInventorySlotDragOver(event, index);
  };

  const handlePendingTabSlotDrop = async (event, index) => {
    if (resolveDraggedPendingItem(event)) {
      await handlePendingDropOnGrid(event, index);
      return;
    }
    handleGridDrop(event, index);
  };

  const handleSellToMarket = async () => {
    if (!activeItemPopup?.item || !username) return;
    const item = activeItemPopup.item;
    const maxCount = item.count || 1;
    const qty = Math.min(Math.max(1, sellQuantity), maxCount);
    setSellLoading(true);
    clearError();
    try {
      const res = await axios.post('/api/inventory/sell', {
        username,
        inventoryType: item.inventoryType,
        userItemId: item.id,
        count: qty
      });
      if (res.data.success) {
        setActiveItemPopup(null);
        setStatus(res.data.message || t('statusSellComplete'));
        const newGold = typeof res.data.newGold === 'number' ? res.data.newGold : undefined;
        await refreshInventoryContent({ refreshGold: true, goldOverride: newGold });
      } else {
        setError(res.data.message || t('errorSellFailed'));
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('errorSellFailed');
      setError(msg);
    } finally {
      setSellLoading(false);
    }
  };

  const getFishOpenKind = (baseItemId) => {
    if (!fishOpeningConfig || baseItemId == null) return null;
    const id = Number(baseItemId);
    if (id === Number(fishOpeningConfig.caviarOtherItemId)) return 'caviar';
    if (Array.isArray(fishOpeningConfig.openableFishOtherIds) && fishOpeningConfig.openableFishOtherIds.includes(id)) {
      return 'fish';
    }
    return null;
  };

  const canCookFish = (baseItemId) => {
    if (!fishOpeningConfig || baseItemId == null) return false;
    const id = Number(baseItemId);
    return Array.isArray(fishOpeningConfig.cookableFishOtherIds)
      && fishOpeningConfig.cookableFishOtherIds.includes(id);
  };

  const canUseCookedFish = (baseItemId) => {
    if (!fishOpeningConfig || baseItemId == null) return false;
    const id = Number(baseItemId);
    return Array.isArray(fishOpeningConfig.cookedFishOtherIds)
      && fishOpeningConfig.cookedFishOtherIds.includes(id);
  };

  const canUseCropFood = (baseItemId) => {
    if (!farmFoodConfig || baseItemId == null) return false;
    const id = Number(baseItemId);
    return Array.isArray(farmFoodConfig.usableCropOtherIds)
      && farmFoodConfig.usableCropOtherIds.includes(id);
  };

  const lootBoostKind = (baseItemId) => {
    const id = Number(baseItemId);
    if (id === 1314) return 'exp';
    if (id === 1315) return 'drop';
    return null;
  };

  const canSmeltOre = (baseItemId) => {
    if (!miningConfig || baseItemId == null) return false;
    const id = Number(baseItemId);
    return Array.isArray(miningConfig.smeltableOreOtherIds)
      && miningConfig.smeltableOreOtherIds.includes(id);
  };

  const canSmeltOreWithCount = (baseItemId, count) => {
    if (!canSmeltOre(baseItemId)) return false;
    return (Number(count) || 0) >= smeltInputCount;
  };

  const smeltInputCount = miningConfig?.smelt?.inputCount ?? miningConfig?.smelt?.orePerAttempt ?? 10;

  const getCookedFishEffectSummary = (baseItemId) => {
    if (!fishOpeningConfig?.cookedFishEffects || baseItemId == null) return null;
    const id = Number(baseItemId);
    const effects = fishOpeningConfig.cookedFishEffects;
    return effects[id] || effects[String(id)] || null;
  };

  const getCropFoodEffectSummary = (baseItemId) => {
    if (!farmFoodConfig?.cropFoodEffects || baseItemId == null) return null;
    const id = Number(baseItemId);
    const effects = farmFoodConfig.cropFoodEffects;
    return effects[id] || effects[String(id)] || null;
  };

  const refreshOthersIfSelected = async () => {
    if (selectedSlot !== 'other') return;
    const invResponse = await axios.get(`/api/inventory?username=${username}&type=others`);
    const itemsResponse = await axios.get('/api/items?type=others');
    if (invResponse.data.success && itemsResponse.data.success) {
      setInventoryItems(invResponse.data.items || []);
      setItems(itemsResponse.data.items || []);
    }
  };

  const handleOpenFishItem = async (userItemId) => {
    if (!username || !userItemId || fishOpenLoading || fishCookLoading || fishUseLoading) return;
    setFishOpenLoading(true);
    clearError();
    try {
      const res = await axios.post('/api/fishing/open', {
        username,
        userItemId: Number(userItemId)
      });
      if (res.data.success) {
        const reward = res.data.reward;
        const rewardText = reward?.name
          ? `${reward.name} x${reward.count || 1}`
          : '';
        setStatus(rewardText ? `${res.data.message} (${rewardText})` : (res.data.message || t('statusFishOpened')));
        setActiveItemPopup(null);
        await loadInventoryBootstrap({ silent: true });
        await refreshOthersIfSelected();
      } else {
        setError(res.data.message || t('errorFishOpenFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorFishOpenFailed'));
    } finally {
      setFishOpenLoading(false);
    }
  };

  const handleCookFishItem = async (userItemId) => {
    if (!username || !userItemId || fishCookLoading || fishOpenLoading || fishUseLoading) return;
    setFishCookLoading(true);
    clearError();
    try {
      const res = await axios.post('/api/fishing/cook', {
        username,
        userItemId: Number(userItemId)
      });
      if (res.data.success) {
        const reward = res.data.reward;
        const rewardText = reward?.name
          ? `${reward.name} x${reward.count || 1}`
          : '';
        setStatus(rewardText ? `${res.data.message} (${rewardText})` : (res.data.message || t('statusFishCooked')));
        setActiveItemPopup(null);
        await loadInventoryBootstrap({ silent: true });
        await refreshOthersIfSelected();
      } else {
        setError(res.data.message || t('errorFishCookFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorFishCookFailed'));
    } finally {
      setFishCookLoading(false);
    }
  };

  const handleUseCookedFishItem = async (userItemId) => {
    if (!username || !userItemId || fishUseLoading || cropUseLoading || lootBoostLoading || fishOpenLoading || fishCookLoading || oreSmeltLoading) return;
    setFishUseLoading(true);
    clearError();
    try {
      const res = await axios.post('/api/fishing/use-cooked', {
        username,
        userItemId: Number(userItemId)
      });
      if (res.data.success) {
        setStatus(res.data.message || t('statusFishUsed'));
        setActiveItemPopup(null);
        await loadInventoryBootstrap({ silent: true });
        await refreshOthersIfSelected();
        await refreshCharacter().catch(() => {});
      } else {
        setError(res.data.message || t('errorFishUseFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorFishUseFailed'));
    } finally {
      setFishUseLoading(false);
    }
  };

  const handleUseCropFoodItem = async (userItemId) => {
    if (!username || !userItemId || cropUseLoading || lootBoostLoading || fishUseLoading || fishOpenLoading || fishCookLoading || oreSmeltLoading) return;
    setCropUseLoading(true);
    clearError();
    try {
      const res = await axios.post('/api/farming/use-crop', {
        username,
        userItemId: Number(userItemId)
      });
      if (res.data.success) {
        setStatus(res.data.message || t('statusCropUsed'));
        setActiveItemPopup(null);
        await loadInventoryBootstrap({ silent: true });
        await refreshOthersIfSelected();
        await refreshCharacter().catch(() => {});
      } else {
        setError(res.data.message || t('errorCropUseFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorCropUseFailed'));
    } finally {
      setCropUseLoading(false);
    }
  };

  const handleUseLootBoostItem = async (userItemId) => {
    if (!username || !userItemId || lootBoostLoading || cropUseLoading || fishUseLoading || fishOpenLoading || fishCookLoading || oreSmeltLoading) return;
    setLootBoostLoading(true);
    clearError();
    try {
      const res = await axios.post('/api/inventory/use-loot-boost', {
        username,
        userItemId: Number(userItemId)
      });
      if (res.data.success) {
        setStatus(res.data.message || t('statusLootBoostUsed'));
        setActiveItemPopup(null);
        await loadInventoryBootstrap({ silent: true });
        await refreshOthersIfSelected();
        await refreshCharacter().catch(() => {});
      } else {
        setError(res.data.message || t('errorLootBoostFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorLootBoostFailed'));
    } finally {
      setLootBoostLoading(false);
    }
  };

  const handleReadRecipeBook = async (otherId) => {
    if (!username || !otherId || recipeBookLoading) return;
    setRecipeBookLoading(true);
    clearError();
    try {
      const result = await readCraftRecipeBook(username, otherId);
      if (result.ok) {
        setStatus(result.message);
        setActiveItemPopup(null);
        await loadInventoryBootstrap({ silent: true });
        await refreshOthersIfSelected();
      } else {
        setError(result.message);
      }
    } finally {
      setRecipeBookLoading(false);
    }
  };

  const handleSmeltOre = async (baseItemId) => {
    if (!username || !baseItemId || oreSmeltLoading || fishOpenLoading || fishCookLoading || fishUseLoading) return;
    setOreSmeltLoading(true);
    clearError();
    try {
      const res = await axios.post('/api/mining/smelt', {
        username,
        oreOtherItemId: Number(baseItemId),
      });
      if (res.data.success) {
        if (typeof res.data?.depotItemCount === 'number') {
          setDepotItemCount?.(res.data.depotItemCount);
        }
        if (res.data.smelted) {
          setStatus(res.data.message || t('statusOreSmelted'));
        } else {
          setError(res.data.message || t('statusOreSmeltFailedPartial'));
        }
        setActiveItemPopup(null);
        await loadInventoryBootstrap({ silent: true });
        await refreshOthersIfSelected();
      } else {
        setError(res.data.message || t('errorOreSmeltFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorOreSmeltFailed'));
    } finally {
      setOreSmeltLoading(false);
    }
  };

  const handleOpenBossStomach = async (otherId) => {
    if (!username || !otherId || bossStomachOpenLoading) return;
    setBossStomachOpenLoading(true);
    clearError();
    try {
      const res = await axios.post('/api/boss-stomach/open', {
        username,
        otherId: Number(otherId)
      });
      if (res.data.success) {
        const loot = Array.isArray(res.data.items) ? res.data.items : [];
        const lootSummary = loot.length > 0
          ? loot.map((item) => `${item.itemName || item.itemType} x${item.count || 1}`).join(', ')
          : '';
        setStatus(lootSummary ? `${res.data.message} (${lootSummary})` : (res.data.message || t('statusBossStomachOpened')));
        setActiveItemPopup(null);
        await loadInventoryBootstrap({ silent: true });
        if (selectedSlot === 'other') {
          const invResponse = await axios.get(`/api/inventory?username=${username}&type=others`);
          const itemsResponse = await axios.get('/api/items?type=others');
          if (invResponse.data.success && itemsResponse.data.success) {
            setInventoryItems(invResponse.data.items || []);
            setItems(itemsResponse.data.items || []);
          }
        }
      } else {
        setError(res.data.message || t('errorBossStomachOpenFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorBossStomachOpenFailed'));
    } finally {
      setBossStomachOpenLoading(false);
    }
  };

  const getEfsunToolForOther = (baseItemId) => {
    if (Number(baseItemId) === EFSUN_OPEN_TOOL_ID) return 'open';
    if (Number(baseItemId) === EFSUN_REROLL_TOOL_ID) return 'reroll';
    if (Number(baseItemId) === EFSUN_CUBE_TOOL_ID) return 'cube';
    return null;
  };

  const getDraggedEfsunTool = (event) => {
    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex === null) return { sourceIndex: null, tool: null, toolItem: null };
    const toolItem = inventoryGridSlots[sourceIndex];
    return {
      sourceIndex,
      tool: getEfsunToolForOther(toolItem?.baseItem?.id),
      toolItem
    };
  };

  const getDraggedIngotId = (eventOrItem) => {
    if (eventOrItem && typeof eventOrItem === 'object' && eventOrItem.baseItem) {
      const id = Number(eventOrItem.baseItem?.id);
      return isIngotItemId(id) ? id : null;
    }
    const sourceIndex = resolveDraggedInventoryIndex(eventOrItem);
    if (sourceIndex === null) return null;
    const item = inventoryGridSlots[sourceIndex];
    const id = Number(item?.baseItem?.id);
    return isIngotItemId(id) ? id : null;
  };

  const canApplyEfsunToolToInventoryTarget = (event, targetIndex) => {
    const { sourceIndex, tool } = getDraggedEfsunTool(event);
    if (!tool || sourceIndex === targetIndex) return false;
    const targetItem = inventoryGridSlots[targetIndex];
    return !!getEfsunTargetKindForItem(targetItem) && !!targetItem.id;
  };

  const canApplyIngotToInventoryTarget = (event, targetIndex) => {
    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex === null || sourceIndex === targetIndex) return false;
    const ingotId = getDraggedIngotId(event);
    if (!ingotId) return false;
    const targetItem = inventoryGridSlots[targetIndex];
    return !!getEfsunTargetKindForItem(targetItem) && !!targetItem?.id;
  };

  const handleApplyPlatingToItem = async (targetKind, userItemId, ingotOtherItemId) => {
    if (!targetKind || !userItemId || !ingotOtherItemId || !username || platingApplyLoading) return;
    setPlatingApplyLoading(true);
    clearError();
    try {
      const res = await axios.post('/api/mining/plate', {
        username,
        kind: targetKind,
        userItemId,
        ingotOtherItemId
      });
      if (res.data.success) {
        if (res.data.plated) {
          setStatus(res.data.message || t('statusPlatingApplied'));
        } else {
          setWarning(res.data.message || t('statusPlatingFailed'));
        }
        setActiveItemPopup(null);
        setActiveEquippedPopup(null);
        await loadInventoryBootstrap({ silent: true });
      } else {
        setError(res.data.message || t('errorPlatingFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorPlatingFailed'));
    } finally {
      setPlatingApplyLoading(false);
    }
  };

  const handleApplyEfsunToItem = async (targetKind, userItemId, tool) => {
    if (!tool || !username || !targetKind || !userItemId || efsunApplyLoading) return;
    let endpoint = '/api/armor-efsun/apply';
    let idPayload = { userArmorId: userItemId };
    if (targetKind === 'weapon') {
      endpoint = '/api/weapon-efsun/apply';
      idPayload = { userWeaponId: userItemId };
    } else if (['helmet', 'boot', 'shield', 'necklace', 'earring'].includes(targetKind)) {
      endpoint = '/api/equipment-efsun/apply';
      idPayload = { kind: targetKind, userItemId };
    }
    setEfsunApplyLoading(true);
    clearError();
    try {
      const res = await axios.post(endpoint, {
        username,
        ...idPayload,
        tool
      });
      if (res.data.success) {
        setStatus(res.data.message || t('statusEnchantApplied'));
        setActiveItemPopup(null);
        setActiveEquippedPopup(null);
        await loadInventoryBootstrap({ silent: true });
      } else {
        setError(res.data.message || t('errorEfsunFailed'));
        if (res.data.code === 'EFSUN_FAILED') {
          setActiveItemPopup(null);
          setActiveEquippedPopup(null);
          await loadInventoryBootstrap({ silent: true });
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('errorEfsunFailed'));
    } finally {
      setEfsunApplyLoading(false);
    }
  };

  const handleSlotClick = async (slotType) => {
    // "Diğer" kategorisi için giyme seçeneği yok, sadece görüntüleme
    if (slotType === 'other') {
      setSelectedSlot('other'); // 'other' olarak ayarla ki ekranda gösterilebilsin
      clearError();
      try {
        const invResponse = await axios.get(
          `/api/inventory?username=${username}&type=others`
        );
        const itemsResponse = await axios.get(
          `/api/items?type=others`
        );
        if (invResponse.data.success && itemsResponse.data.success) {
          setInventoryItems(invResponse.data.items || []);
          setItems(itemsResponse.data.items || []);
        } else {
          setInventoryItems([]);
          setItems([]);
        }
      } catch (err) {
        setError(t('errorItemsLoadFailed'));
      }
      return;
    }

    setSelectedSlot(slotType);
    clearError();

    try {
      // Kullanıcının envanterindeki itemları getir
      let itemTypePlural;
      if (slotType === 'shield') {
        itemTypePlural = 'shields';
      } else if (isPotSlotType(slotType) || slotType === 'pot') {
        itemTypePlural = 'pots';
      } else {
        itemTypePlural = `${slotType}s`;
      }
      const invResponse = await axios.get(
        `/api/inventory?username=${username}&type=${itemTypePlural}`
      );
      if (invResponse.data.success && invResponse.data.items) {
        setInventoryItems(invResponse.data.items);
      } else {
        setInventoryItems([]);
      }

      // Temel item tanımlarını getir (detaylar için)
      const response = await axios.get(
        `/api/items?type=${itemTypePlural}`
      );
      if (response.data.success && response.data.items) {
        setItems(response.data.items);
      } else {
        setItems([]);
      }
    } catch (err) {
      setError(t('errorItemsLoadFailed'));
    }
  };

  const handleItemSelect = async (userItemId) => {
    if (!selectedSlot) return;

    try {
      clearError(); // Hata mesajını temizle
      const currentlyEquippedItemId = getEquippedUserItemId(selectedSlot);
      if (currentlyEquippedItemId && currentlyEquippedItemId !== userItemId) {
        setError(t('errorUnequipFirst'));
        return;
      }
      
      const invItem = inventoryItems.find((item) => item.id === userItemId);
      const baseItemId = invItem ? getPotBaseItemId(invItem) : null;
      const baseItem =
        baseItemId && items?.find ? items.find((item) => item.id === baseItemId) : null;
      const slotItemForCheck = invItem && baseItem
        ? {
            ...invItem,
            slotType:
              isPotSlotType(selectedSlot) ? 'pot' : selectedSlot,
            baseItem,
          }
        : null;

      if (!canEquipItemToSlot(slotItemForCheck, selectedSlot)) {
        if (!slotItemForCheck) {
          setError(t('errorItemInfoNotFound'));
        } else if (!isItemCompatible(baseItem)) {
          setError(t('errorCharacterMismatch'));
        } else if (!isLevelSufficient(baseItem)) {
          setError(t('errorLevelInsufficient', { level: baseItem.level }));
        } else if (isPotSlotType(selectedSlot)) {
          setError(t('errorSamePotTwice'));
        } else {
          setError(t('errorEquipSlotMismatch'));
        }
        return;
      }

      const response = await axios.post('/api/equip', {
        username,
        itemType: selectedSlot,
        itemId: userItemId, // user_weapons, user_armors vb. tablosundaki ID
      });

      if (response.data.success) {
        // Ekipmanı güncelle
        await loadInventoryBootstrap({ silent: true });
        // State'i temizle ve ana ekrana dön
        setSelectedSlot(null);
        setItems([]);
        setInventoryItems([]);
      } else {
        setError(response.data.message || t('errorEquipFailed'));
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || t('errorEquipFailed');
      setError(errorMessage);
      console.error('Error equipping item:', err);
    }
  };

  const handleUnequip = async (slotType) => {
    try {
      clearError(); // Hata mesajını temizle
      const response = await axios.post('/api/unequip', {
        username,
        itemType: slotType,
      });

      if (response.data.success) {
        if (response.data.miningPaused) {
          setStatus(t('statusMiningPaused'));
        }
        await loadInventoryBootstrap({ silent: true });
        // Ana ekrana dön
        setSelectedSlot(null);
        setItems([]);
        setInventoryItems([]);
      } else {
        setError(response.data.message || t('errorUnequipFailed'));
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || t('errorUnequipFailed');
      setError(errorMessage);
      console.error('Error unequipping item:', err);
    }
  };

  const handleBack = () => {
    navigate(location.state?.from || '/game');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'inventory' ? {} : { tab });
  };

  // Diğer item'lar için sadece görüntüleme ekranı (giyilemez)
  if (selectedSlot === 'other') {
    return (
      <div className="auth-container auth-container--selection">
        <AuthLayeredCard>
          <AuthSelectionToolbar
            onBack={() => {
              setSelectedSlot(null);
              setInventoryItems([]);
              setItems([]);
            }}
            title={t('otherItemsTitle')}
            buttonStyle={authButtonStyle}
          />

          <div
            className="auth-selection-body inventory-auth-body"
            style={inventoryMenuBgCssVars}
          >
          <div className="items-list">
            {inventoryItems && inventoryItems.length > 0 ? (
              <div>
                <div className="items-grid">
                  {inventoryItems.map((invItem) => {
                    const baseItemId = invItem.otherId;
                    const baseItem = items && items.find ? items.find(item => item.id === baseItemId) : null;
                    if (!baseItem) return null;
                    const itemCount = invItem.count || 1;
                    return (
                      <div
                        key={invItem.id}
                        className="item-card"
                      >
                        <div className="item-image-placeholder">
                          {baseItem.imageUrl ? (
                            <ItemImage
                              imageUrl={baseItem.imageUrl}
                              alt={baseItem.name}
                              singleFrameSprite={selectedSlot === 'boot'}
                            />
                          ) : (
                            <div className="placeholder-icon">📦</div>
                          )}
                        </div>
                        <div className="item-header">
                          <h3>
                            {baseItem.name}
                            {itemCount > 1 && (
                              <span style={{
                                marginLeft: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#8b7355'
                              }}>
                                x{itemCount}
                              </span>
                            )}
                          </h3>
                        </div>
                        <p>{tc('level')}: {baseItem.level}</p>
                        {baseItem.value !== undefined && (
                          <p>{t('popupValue', { amount: baseItem.value })}</p>
                        )}
                        {getFishOpenKind(baseItemId) && (
                          <button
                            type="button"
                            className="auth-button auth-button--compact"
                            style={authButtonStyle}
                            disabled={fishOpenLoading || fishCookLoading || fishUseLoading}
                            onClick={() => handleOpenFishItem(invItem.id)}
                          >
                            {fishOpenLoading ? '…' : t('popupOpenFish')}
                          </button>
                        )}
                        {canCookFish(baseItemId) && (
                          <button
                            type="button"
                            className="auth-button auth-button--compact"
                            style={authButtonStyle}
                            disabled={fishCookLoading || fishOpenLoading || fishUseLoading || oreSmeltLoading}
                            onClick={() => handleCookFishItem(invItem.id)}
                          >
                            {fishCookLoading ? '…' : t('popupCookFish')}
                          </button>
                        )}
                        {canSmeltOre(baseItemId) && (
                          <button
                            type="button"
                            className="auth-button auth-button--compact"
                            style={authButtonStyle}
                            disabled={oreSmeltLoading || fishOpenLoading || fishCookLoading || fishUseLoading || !canSmeltOreWithCount(baseItemId, itemCount)}
                            onClick={() => handleSmeltOre(baseItemId)}
                          >
                            {oreSmeltLoading ? '…' : t('popupSmeltOre', { count: smeltInputCount })}
                          </button>
                        )}
                        {canUseCookedFish(baseItemId) && (
                          <>
                            {getCookedFishEffectSummary(baseItemId) && (
                              <p className="inventory-fish-effect">
                                {t('popupCookedFishEffect', { effect: getCookedFishEffectSummary(baseItemId) })}
                              </p>
                            )}
                            <button
                              type="button"
                              className="auth-button auth-button--compact"
                              style={authButtonStyle}
                              disabled={fishUseLoading || cropUseLoading || fishOpenLoading || fishCookLoading}
                              onClick={() => handleUseCookedFishItem(invItem.id)}
                            >
                              {fishUseLoading ? '…' : t('popupUseFish')}
                            </button>
                          </>
                        )}
                        {canUseCropFood(baseItemId) && (
                          <>
                            {getCropFoodEffectSummary(baseItemId) && (
                              <p className="inventory-fish-effect">
                                {t('popupCropFoodEffect', { effect: getCropFoodEffectSummary(baseItemId) })}
                              </p>
                            )}
                            <button
                              type="button"
                              className="auth-button auth-button--compact"
                              style={authButtonStyle}
                              disabled={cropUseLoading || fishUseLoading || fishOpenLoading || fishCookLoading}
                              onClick={() => handleUseCropFoodItem(invItem.id)}
                            >
                              {cropUseLoading ? '…' : t('popupUseCrop')}
                            </button>
                          </>
                        )}
                        {lootBoostKind(baseItemId) && (
                          <>
                            <p className="inventory-fish-effect">
                              {t('popupLootBoostEffect', {
                                effect: lootBoostKind(baseItemId) === 'exp'
                                  ? t('lootBoostExpEffect')
                                  : t('lootBoostDropEffect')
                              })}
                            </p>
                            <button
                              type="button"
                              className="auth-button auth-button--compact"
                              style={authButtonStyle}
                              disabled={lootBoostLoading || cropUseLoading || fishUseLoading || fishOpenLoading || fishCookLoading}
                              onClick={() => handleUseLootBoostItem(invItem.id)}
                            >
                              {lootBoostLoading ? '…' : t('popupUseCrop')}
                            </button>
                          </>
                        )}
                        {baseItem.recipeBookLevel ? (
                          <button
                            type="button"
                            className="auth-button auth-button--compact"
                            style={authButtonStyle}
                            disabled={recipeBookLoading}
                            onClick={() => handleReadRecipeBook(baseItemId)}
                          >
                            {recipeBookLoading ? '…' : t('craft:readBook')}
                          </button>
                        ) : null}
                        {baseItem.openable && !getFishOpenKind(baseItemId) && (
                          <button
                            type="button"
                            className="auth-button auth-button--compact"
                            style={authButtonStyle}
                            disabled={bossStomachOpenLoading}
                            onClick={() => handleOpenBossStomach(baseItemId)}
                          >
                            {bossStomachOpenLoading ? '…' : t('popupOpenBossStomach')}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="empty-inventory">
                <p>{t('selectEmptyInCategory', { category: t('slotOther') })}</p>
              </div>
            )}
          </div>
          </div>
        </AuthLayeredCard>
      </div>
    );
  }

  // Slot seçimi ekranı (giyilebilir item'lar için)
  if (selectedSlot) {
    const slotSelectTitle = getSlotSelectTitle(selectedSlot);

    return (
      <div className="auth-container auth-container--selection">
        <AuthLayeredCard>
          <AuthSelectionToolbar
            onBack={() => setSelectedSlot(null)}
            title={slotSelectTitle}
            subtitle={t('selectSubtitle')}
            buttonStyle={authButtonStyle}
          />

          <div
            className="auth-selection-body inventory-auth-body"
            style={inventoryMenuBgCssVars}
          >
          <div className="items-list">
            {inventoryItems && inventoryItems.length > 0 ? (
              <div>
                <div className="items-select">
                  <select 
                    className="item-select"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "unequip") {
                        handleUnequip(selectedSlot);
                      } else if (value !== "") {
                        const itemId = parseInt(value);
                        if (itemId > 0) {
                          handleItemSelect(itemId);
                        }
                      }
                    }}
                    value={(() => {
                      // Mevcut giyili item'ı equipment'tan bul
                      if (equipment) {
                        let currentItemId = null;
                        switch (selectedSlot) {
                          case 'weapon':
                            currentItemId = equipment.weaponId;
                            break;
                          case 'armor':
                            currentItemId = equipment.armorId;
                            break;
                          case 'helmet':
                            currentItemId = equipment.helmetId;
                            break;
                          case 'boot':
                            currentItemId = equipment.bootId;
                            break;
                          case 'shield':
                            currentItemId = equipment.shieldId;
                            break;
                          case 'earring':
                            currentItemId = equipment.earringId;
                            break;
                          case 'necklace':
                            currentItemId = equipment.necklaceId;
                            break;
                          case 'pot1':
                            currentItemId = equipment.potId;
                            break;
                          case 'pot2':
                            currentItemId = equipment.potId2;
                            break;
                          case 'pot3':
                            currentItemId = equipment.potId3;
                            break;
                          case 'pot4':
                            currentItemId = equipment.potId4;
                            break;
                          default:
                            currentItemId = null;
                            break;
                        }
                        return currentItemId ? currentItemId.toString() : "unequip";
                      }
                      return "unequip";
                    })()}
                  >
                    <option value="unequip">{tc('empty')}</option>
                    {inventoryItems && inventoryItems.map((invItem) => {
                      // Base item ID'yi bul
                      const baseItemId = invItem.weaponId || invItem.armorId || invItem.helmetId || invItem.bootId || invItem.earringId || invItem.necklaceId || invItem.shieldId || invItem.potId || invItem.otherId;
                      const baseItem = items && items.find ? items.find(item => item.id === baseItemId) : null;
                      if (!baseItem) return null;
                      const slotItemForCheck = {
                        ...invItem,
                        slotType:
                          isPotSlotType(selectedSlot) ? 'pot' : selectedSlot,
                        baseItem,
                      };
                      const canEquip = canEquipItemToSlot(slotItemForCheck, selectedSlot);
                      const itemCount = invItem.count || 1;
                      const countText = itemCount > 1 ? t('selectCountSuffix', { count: itemCount }) : '';
                      return (
                        <option 
                          key={invItem.id} 
                          value={invItem.id}
                          disabled={!canEquip}
                        >
                          {getCharacterName(baseItem.characterId)} - {baseItem.name}{countText} ({tc('enhancement')}: +{baseItem.enhancement || 0}, {tc('level')}: {baseItem.level})
                          {!canEquip && ` ${t('selectNotCompatible')}`}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="items-grid">
                  {inventoryItems && inventoryItems.map((invItem) => {
                    // Base item ID'yi bul
                    const baseItemId = invItem.weaponId || invItem.armorId || invItem.helmetId || invItem.bootId || invItem.earringId || invItem.necklaceId || invItem.shieldId || invItem.potId || invItem.otherId;
                    const baseItem = items && items.find ? items.find(item => item.id === baseItemId) : null;
                    if (!baseItem) return null;
                    const slotItemForCheck = {
                      ...invItem,
                      slotType:
                        isPotSlotType(selectedSlot) ? 'pot' : selectedSlot,
                      baseItem,
                    };
                    const canEquip = canEquipItemToSlot(slotItemForCheck, selectedSlot);
                    const itemCount = invItem.count || 1;
                    return (
                      <div
                        key={invItem.id}
                        className={`item-card ${canEquip ? 'item-equip-ok' : 'item-equip-no'}`}
                        onClick={() => {
                          if (canEquip) {
                            handleItemSelect(invItem.id);
                          }
                        }}
                        style={{
                          cursor: canEquip ? 'pointer' : 'not-allowed',
                        }}
                      >
                        <div className="item-image-placeholder">
                          {baseItem.imageUrl ? (
                            <ItemImage imageUrl={baseItem.imageUrl} alt={baseItem.name} />
                          ) : (
                            <div className="placeholder-icon">📦</div>
                          )}
                        </div>
                        <div className="item-header">
                          <span
                            className={`character-badge ${
                              baseItem.characterId === 0
                                ? 'character-badge--all'
                                : baseItem.characterId === 1
                                  ? 'character-badge--warrior'
                                  : 'character-badge--sura'
                            }`}
                          >
                            {getCharacterName(baseItem.characterId)}
                          </span>
                          <h3>
                            {baseItem.name}
                            {itemCount > 1 && (
                              <span style={{ 
                                marginLeft: '8px', 
                                fontSize: '16px', 
                                fontWeight: '600', 
                                color: '#8b7355' 
                              }}>
                                x{itemCount}
                              </span>
                            )}
                          </h3>
                        </div>
                        <p>{tc('enhancement')}: +{baseItem.enhancement || 0}</p>
                        <p>{tc('level')}: {baseItem.level}</p>
                        {!canEquip && (
                          <p className="item-equip-no-hint">
                            {!isItemCompatible(baseItem) && `${t('hintCharacterMismatch')} `}
                            {isItemCompatible(baseItem) && !isLevelSufficient(baseItem) &&
                              `${t('hintLevelInsufficient', { level: baseItem.level })} `}
                            {isItemCompatible(baseItem) &&
                              isLevelSufficient(baseItem) &&
                              isPotSlotType(selectedSlot) &&
                              `${t('hintPotSlotMismatch')} `}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="empty-inventory">
                <p>{t('selectEmptyInCategory', { category: getEmptyCategoryLabel(selectedSlot) })}</p>
              </div>
            )}
          </div>
          </div>
        </AuthLayeredCard>
      </div>
    );
  }


  const buildItemPopupProps = () => {
    const item = activeItemPopup?.item;
    if (!item) return null;
    const baseItemId = item.baseItem?.id;
    const cookedEffect = getCookedFishEffectSummary(baseItemId);
    const cropEffect = getCropFoodEffectSummary(baseItemId);
    return {
      item,
      onClose: () => setActiveItemPopup(null),
      closeLabel: tc('close'),
      renderItemNameWithPlating,
      renderItemPlatingBlock,
      categoryLabel: tc('category'),
      characterName: {
        label: tc('character'),
        value: getCharacterName(item.baseItem.characterId)
      },
      levelLabel: tc('level'),
      enhancementLabel: tc('enhancement'),
      statRows: buildItemStatRows({
        ...item.baseItem,
        categoryLabel: item.categoryLabel,
        count: item.count || 1,
        plating: item.plating
      }),
      renderStatRow,
      efsunCountLabel: getEfsunTargetKindForItem(item)
        && formatEfsunCountLabel(item.efsunlar, getEfsunMaxForItem(item))
        ? formatEfsunCountLabel(item.efsunlar, getEfsunMaxForItem(item))
        : null,
      efsunRows: getEfsunTargetKindForItem(item)
        ? buildEfsunRows(
          item.efsunlar,
          getEfsunTargetKindForItem(item) || 'armor'
        )
        : [],
      compatibilityLabel: `${t('popupCompatibility')}: ${isItemCompatible(item.baseItem) && isLevelSufficient(item.baseItem) ? t('selectCompatibilityOk') : t('selectCompatibilityNo')}`,
      fishOpenKind: getFishOpenKind(baseItemId),
      canCookFish: canCookFish(baseItemId),
      canSmeltOre: canSmeltOre(baseItemId),
      canSmeltOreWithCount: canSmeltOreWithCount(baseItemId, item.count),
      smeltInputCount,
      canUseCookedFish: canUseCookedFish(baseItemId),
      cookedFishEffect: cookedEffect ? t('popupCookedFishEffect', { effect: cookedEffect }) : null,
      canUseCropFood: canUseCropFood(baseItemId),
      cropFoodEffect: cropEffect ? t('popupCropFoodEffect', { effect: cropEffect }) : null,
      isBossStomachOpenable: item.baseItem?.openable && !getFishOpenKind(baseItemId),
      fishOpenLoading,
      fishCookLoading,
      fishUseLoading,
      cropUseLoading,
      oreSmeltLoading,
      bossStomachOpenLoading,
      onOpenFish: handleOpenFishItem,
      onCookFish: handleCookFishItem,
      onSmeltOre: handleSmeltOre,
      onUseCookedFish: handleUseCookedFishItem,
      onUseCropFood: handleUseCropFoodItem,
      onOpenBossStomach: handleOpenBossStomach,
      isRecipeBook: Boolean(item.baseItem?.recipeBookLevel),
      recipeBookLoading,
      onReadRecipeBook: handleReadRecipeBook,
      readRecipeBookLabel: t('craft:readBook'),
      npcSellLabel: t('popupNpcSell'),
      sellButtonLabel: t('popupSellToMarket'),
      sellQuantity,
      onDecreaseSellQuantity: () => setSellQuantity((q) => Math.max(1, q - 1)),
      onIncreaseSellQuantity: () => setSellQuantity((q) => Math.min(item.count || 1, q + 1)),
      onSellToMarket: handleSellToMarket,
      sellLoading,
      sellTotalLabel: t('popupTotal', {
        amount: (Number(item.baseItem.sellValue ?? item.baseItem.value) || 0) * sellQuantity
      }),
      openFishLabel: t('popupOpenFish'),
      cookFishLabel: t('popupCookFish'),
      smeltOreLabel: t('popupSmeltOre', { count: smeltInputCount }),
      useFishLabel: t('popupUseFish'),
      useCropLabel: t('popupUseCrop'),
      openBossStomachLabel: t('popupOpenBossStomach'),
      decreaseQtyLabel: t('popupDecreaseQty'),
      increaseQtyLabel: t('popupIncreaseQty')
    };
  };

  const itemPopupProps = buildItemPopupProps();

  // Ana envanter ekranı
  return (
    <div className="auth-container auth-container--selection">
      <AuthLayeredCard>
        <div className="battle-nav-row">
          <button
            type="button"
            className="auth-button auth-button--compact battle-nav-btn battle-nav-btn--back"
            style={authButtonSmallStyle}
            onClick={handleBack}
            aria-label={tc('back')}
          >
            ←
          </button>
          <button
            type="button"
            className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'inventory' ? ' battle-subtab--active' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => handleTabChange('inventory')}
          >
            {t('tabsInventory')}
          </button>
          <button
            type="button"
            className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'development' ? ' battle-subtab--active' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => handleTabChange('development')}
          >
            {t('tabsDevelopment')}
          </button>
          <button
            type="button"
            className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'pending' ? ' battle-subtab--active' : ''}${activeTab !== 'pending' && (enrichedPendingItems.length > 0 || friendGifts.length > 0) ? ' depot-btn--alert' : ''}`}
            style={authButtonSmallStyle}
            data-depot-button
            data-tour="inventory-pending"
            onClick={() => handleTabChange('pending')}
          >
            {enrichedPendingItems.length > 0
              ? t('tabsStorageCount', { count: enrichedPendingItems.length })
              : t('tabsStorage')}
          </button>
        </div>

        {activeTab === 'inventory' ? (
          <div
          className="auth-selection-body inventory-auth-body"
          style={inventoryMenuBgCssVars}
          onClickCapture={handleClosePopupsOnCardClickCapture}
          onTouchMoveCapture={handleGridTouchMove}
          onTouchEndCapture={handleGridTouchEnd}
          onTouchCancelCapture={handleGridTouchCancel}
          onTouchMove={handleGridTouchMove}
          onTouchEnd={handleGridTouchEnd}
          onTouchCancel={handleGridTouchCancel}
        >
        {refreshing && (
          <div className="inventory-refresh-overlay" aria-busy="true">
            <span className="inventory-refresh-spinner">{tc('loading')}</span>
          </div>
        )}
        {/* Giyili Itemler */}
        <Coachmark
          tourId="inventory"
          enabled={activeTab === 'inventory'}
          steps={[
            { selector: '.equipped-items-section', textKey: 'inventoryEquipped' },
            { selector: '[data-tour="inventory-pending"]', textKey: 'inventoryPending' },
          ]}
        />
        <div className="equipped-items-section">
          <div className="equipped-grid-area" ref={equippedGridAreaRef}>
            <div className="equipped-items-grid">
            {(() => {
              const equippedBySlotType = new Map(
                (equippedItems || [])
                  .filter((item) => item?.slotType && (item.itemId || item.name))
                  .map((item) => [item.slotType, item])
              );
              const { coveredIndexes: equippedCoveredIndexes, spanByIndex: equippedSpanByIndex } = computeEquippedCoverage(equippedBySlotType);
              const draggedGridItem =
                draggedSlotIndex !== null ? inventoryGridSlots[draggedSlotIndex] : null;
              return Array.from({ length: EQUIPPED_GRID_SIZE }, (_, idx) => {
              const gridIndex = idx + 1;
              const slotConfig = EQUIPPED_SLOT_MAP[gridIndex];
              const isDisabledCell = !slotConfig || !slotConfig.enabled;
              const equippedItem = slotConfig?.slotType
                ? (equippedBySlotType.get(slotConfig.slotType) || null)
                : null;
              const colStart = ((gridIndex - 1) % EQUIPPED_GRID_COLUMNS) + 1;
              const rowStart = Math.floor((gridIndex - 1) / EQUIPPED_GRID_COLUMNS) + 1;
              const span = equippedSpanByIndex[gridIndex];

              if (isDisabledCell || equippedCoveredIndexes.has(gridIndex)) return null;

              let equipDropHighlight = '';
              if (
                dragOverEquipIndex === gridIndex &&
                draggedGridItem &&
                slotConfig?.slotType
              ) {
                const isEfsunToolOnEquippedItem =
                  getEfsunToolForOther(draggedGridItem?.baseItem?.id) &&
                  getEfsunTargetKindForItem(equippedItem) &&
                  equippedItem;
                const isIngotOnEquippedItem =
                  isIngotItemId(draggedGridItem?.baseItem?.id) &&
                  getEfsunTargetKindForItem(equippedItem) &&
                  equippedItem;
                equipDropHighlight = isEfsunToolOnEquippedItem || isIngotOnEquippedItem || canEquipItemToSlot(draggedGridItem, slotConfig.slotType, draggedSlotIndex)
                  ? 'drop-target'
                  : 'drop-target-invalid';
              }

              return (
                <div
                  key={`equipped-grid-${gridIndex}`}
                  className={`inventory-slot-box equipped-item-card ${span ? 'multi-size' : ''} ${equippedItem ? 'filled has-item' : 'empty-slot'} ${equipDropHighlight}`}
                  data-equip-grid-index={gridIndex}
                  data-equip-slot-type={slotConfig.slotType}
                  draggable={!!(slotConfig?.slotType && equippedItem)}
                  style={{
                    gridColumnStart: colStart,
                    gridRowStart: rowStart,
                    ...(span ? buildSpanGridStyle(span) : {})
                  }}
                  onDragStart={(event) => {
                    if (slotConfig?.slotType && equippedItem) {
                      const ghostUrl = buildItemImageUrl(equippedItem?.imageUrl, true);
                      setNativeDragGhost(event, ghostUrl);
                      setDraggedEquipSlotType(slotConfig.slotType);
                    }
                  }}
                  onDragEnd={() => setDraggedEquipSlotType(null)}
                  onTouchStart={(event) => handleEquipTouchStart(event, slotConfig, equippedItem)}
                  onClick={(event) => {
                    if (equippedItem) {
                      handleEquippedItemClick(gridIndex, slotConfig, equippedItem);
                    } else if (slotConfig?.slotType && !isTouchDragging) {
                      handleSlotClick(slotConfig.slotType);
                    }
                  }}
                  onDoubleClick={() => {
                    if (slotConfig?.slotType && equippedItem) {
                      handleSlotClick(slotConfig.slotType);
                    }
                  }}
                  onDragOver={(event) => handleEquipSlotDragOver(event, slotConfig, gridIndex)}
                  onDragLeave={() => setDragOverEquipIndex((prev) => (prev === gridIndex ? null : prev))}
                  onDrop={(event) => handleEquipSlotDrop(event, slotConfig)}
                >
                  {equippedItem ? (
                    <div className="slot-item-image">
                      {equippedItem.imageUrl ? (
                        <img src={`/${equippedItem.imageUrl}`} alt={equippedItem.name} />
                      ) : null}
                      {isPotSlotType(slotConfig.slotType) && equippedItem.count > 0 && (
                        <span className="slot-item-count">{equippedItem.count}</span>
                      )}
                    </div>
                  ) : (
                    <div className="equipped-slot-label">{getSlotLabel(slotConfig.labelKey)}</div>
                  )}
                </div>
              );
            });
            })()}
            </div>
            {activeEquippedPopup && (
              <div className="inventory-item-popup-backdrop" onClick={() => setActiveEquippedPopup(null)}>
                <div className="inventory-item-popup inventory-item-popup--modal" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    className="inventory-item-popup-close"
                    onClick={() => setActiveEquippedPopup(null)}
                    aria-label={tc('close')}
                  >
                    X
                  </button>
                  <div className="inventory-popup-body">
                    <div className="inventory-popup-details">
                      <h4>{activeEquippedPopup.slotLabel}</h4>
                      {activeEquippedPopup.item ? (
                        <>
                          <p>
                            {t('popupItem')}:{' '}
                            <span style={{ color: getActivePlating(activeEquippedPopup.item)?.color || undefined }}>
                              {activeEquippedPopup.item.name}
                              {getActivePlating(activeEquippedPopup.item) ? (
                                <span className="inventory-plating-name-tag"> {getActivePlating(activeEquippedPopup.item).label}</span>
                              ) : null}
                            </span>
                          </p>
                          {renderItemPlatingBlock(activeEquippedPopup.item)}
                          {activeEquippedPopup.item.characterId !== undefined && (
                            <p>{tc('character')}: {getCharacterName(activeEquippedPopup.item.characterId)}</p>
                          )}
                          <p>{tc('level')}: {activeEquippedPopup.item.level}</p>
                          <p>{tc('enhancement')}: +{activeEquippedPopup.item.enhancement || 0}</p>
                          {buildItemStatRows(activeEquippedPopup.item).map((row, index) =>
                            renderStatRow(row, `equipped-popup-${row.label}-${index}`)
                          )}
                          {getEfsunTargetKindForItem(activeEquippedPopup.item) && formatEfsunCountLabel(activeEquippedPopup.item.efsunlar, getEfsunMaxForItem(activeEquippedPopup.item)) && (
                            <p className="inventory-efsun-header">
                              {formatEfsunCountLabel(activeEquippedPopup.item.efsunlar, getEfsunMaxForItem(activeEquippedPopup.item))}
                            </p>
                          )}
                          {getEfsunTargetKindForItem(activeEquippedPopup.item) &&
                          buildEfsunRows(
                            activeEquippedPopup.item.efsunlar,
                            getEfsunTargetKindForItem(activeEquippedPopup.item) || 'armor'
                          ).map((row, index) => (
                            <p key={`equipped-efsun-${row.label}-${index}`} className="inventory-efsun-row">
                              {row.label}: <span className={row.colorClass}>{row.value}</span>
                            </p>
                          ))}
                          <SocketSlots item={activeEquippedPopup.item} />
                        </>
                      ) : (
                        <p>{t('popupSlotEmpty')}</p>
                      )}
                    </div>
                    {activeEquippedPopup.item?.imageUrl ? (
                      <div className="inventory-popup-image">
                        <ItemImage
                          imageUrl={activeEquippedPopup.item.imageUrl}
                          alt={activeEquippedPopup.item.name}
                          singleFrameSprite={isSpriteStripUrl(activeEquippedPopup.item.imageUrl)}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="inventory-currency-row">
          <span className="inventory-currency-item">
            {tc('yang')}: <strong>{displayGold}</strong>
          </span>
          <span className="inventory-currency-item">
            {tc('soulMoney')}: <strong>{displayRuhParasi}</strong>
          </span>
        </div>

        {/* Envanter Listesi */}
        <div className="inventory-list">
          <InventoryBagGrid
            gridSlots={inventoryGridSlots}
            currentInventoryPage={currentInventoryPage}
            onPageChange={setCurrentInventoryPage}
            unlockedSlotsCount={unlockedSlotsCount}
            dragOverIndex={touchDropTargetIndex ?? dragOverInventoryIndex}
            isDropBlocked={(absoluteIndex) => isInventoryDropBlocked(absoluteIndex)}
            onDragStart={handleGridDragStart}
            onDragOver={handleInventorySlotDragOver}
            onDragLeave={(absoluteIndex) => setDragOverInventoryIndex((prev) => (prev === absoluteIndex ? null : prev))}
            onDrop={handleGridDrop}
            onDragEnd={() => {
              setDragOverInventoryIndex(null);
              setDraggedSlotIndex(null);
              setDraggedInventoryItemKey(null);
            }}
            onTouchStart={handleGridTouchStart}
            onClick={(event, slotItem, absoluteIndex) => handleGridItemClick(slotItem, absoluteIndex)}
            slotKeyPrefix="inventory"
            areaRef={inventoryGridAreaRef}
            emptySlotTitle={t('slotEmpty')}
            lockedSlotTitle={t('slotLocked')}
            onPageTabDragOver={handleInventoryTabDragOver}
            onPageTabTouchMove={handleGridTouchMove}
            onPageTabTouchEnd={handleGridTouchEnd}
            isTouchDragging={isTouchDragging}
          />
          {touchDragGhost && (
            <div
              className="touch-drag-ghost"
              style={{
                left: `${touchDragGhost.x}px`,
                top: `${touchDragGhost.y}px`
              }}
            >
              {touchDragGhost.imageUrl ? (
                <img src={touchDragGhost.imageUrl} alt="drag-ghost" />
              ) : (
                <div className="slot-placeholder-icon">📦</div>
              )}
            </div>
          )}
          {allInventoryItems.length > GRID_SIZE && (
            <p className="inventory-grid-limit-note">
              {t('gridLimitNote', { total: allInventoryItems.length, shown: GRID_SIZE })}
            </p>
          )}
        </div>
        </div>
        ) : activeTab === 'pending' ? (
          <div
            className="auth-selection-body inventory-auth-body pending-inventory-body"
            style={inventoryMenuBgCssVars}
            onDragOver={handlePendingAreaDragOver}
            onTouchMoveCapture={handleGridTouchMove}
            onTouchEndCapture={handleGridTouchEnd}
            onTouchCancelCapture={handleGridTouchCancel}
          >
            <PendingInventoryPanel
              pendingItems={pendingItems}
              catalog={itemCatalog}
              loading={pendingLoading}
              actionLoading={pendingActionLoading}
              onClaim={handleClaimPendingItem}
              onSell={handleSellPendingItem}
              onDiscard={handleDiscardPendingItem}
              onPendingDragStart={handlePendingDragStart}
              onPendingTouchStart={handlePendingTouchStart}
              onPendingPressStart={handlePendingPressStart}
              onPendingPressEnd={handlePendingPressEnd}
              onPendingDragEnd={() => {
                unlockTouchDragScroll();
                setTimeout(() => {
                  clearActivePendingDrag();
                }, 0);
              }}
              canSmeltOre={canSmeltOre}
              smeltInputCount={smeltInputCount}
              onSmeltOre={handleSmeltOre}
              oreSmeltLoading={oreSmeltLoading}
              gifts={friendGifts}
              onClaimGift={handleClaimFriendGift}
            />
            <div
              className="inventory-list pending-inventory-list"
              onDragOver={handlePendingAreaDragOver}
            >
              <h2>{t('tabsInventory')}</h2>
              <p className="pending-inventory-hint">{t('pendingDropHint')}</p>
              <InventoryBagGrid
                gridSlots={inventoryGridSlots}
                currentInventoryPage={currentInventoryPage}
                onPageChange={setCurrentInventoryPage}
                unlockedSlotsCount={unlockedSlotsCount}
                dragOverIndex={touchDropTargetIndex ?? dragOverInventoryIndex}
                isDropBlocked={(absoluteIndex) => isPendingTabDropBlocked(absoluteIndex, null)}
                onDragStart={handleGridDragStart}
                onDragOver={handlePendingTabSlotDragOver}
                onDragLeave={(absoluteIndex) => setDragOverInventoryIndex((prev) => (prev === absoluteIndex ? null : prev))}
                onDrop={handlePendingTabSlotDrop}
                onDragEnd={() => {
                  setDragOverInventoryIndex(null);
                  setDraggedSlotIndex(null);
                  setDraggedInventoryItemKey(null);
                }}
                onTouchStart={handleGridTouchStart}
                onClick={(event, slotItem, absoluteIndex) => handleGridItemClick(slotItem, absoluteIndex)}
                slotKeyPrefix="pending-inventory"
                areaRef={inventoryGridAreaRef}
                emptySlotTitle={t('slotEmpty')}
                lockedSlotTitle={t('slotLocked')}
                onPageTabDragOver={handleInventoryTabDragOver}
                onPageTabTouchMove={handleGridTouchMove}
                onPageTabTouchEnd={handleGridTouchEnd}
                isTouchDragging={isTouchDragging}
              />
            </div>
            {touchDragGhost && (
              <div
                className="touch-drag-ghost"
                style={{
                  left: `${touchDragGhost.x}px`,
                  top: `${touchDragGhost.y}px`
                }}
              >
                {touchDragGhost.imageUrl ? (
                  <img src={touchDragGhost.imageUrl} alt="drag-ghost" />
                ) : (
                  <div className="slot-placeholder-icon">📦</div>
                )}
              </div>
            )}
          </div>
        ) : (
          <DevelopmentPanel />
        )}
        {itemPopupProps && (activeTab === 'inventory' || activeTab === 'pending') ? (
          <InventoryItemPopup {...itemPopupProps} />
        ) : null}
      </AuthLayeredCard>
    </div>
  );
};

export default Inventory;

